import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Edit, Trash2, Search, User, Ban, CheckCircle, Home, Briefcase, Users, Download, FileSpreadsheet, FileText, FileJson, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, ArrowUp, ArrowDown, ArrowUpDown, AlertCircle, ThumbsUp, ThumbsDown, Eye, Printer, Building2, X, ChevronDown, ChevronUp, Loader2, KeyRound } from "lucide-react";
import { CatastralInput } from "../components/CatastralInput";
import { SectorCombobox } from "../components/SectorCombobox";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { loadPalmaLogo, drawPalmaFooter, fechaHoraVE } from "../lib/pdfFooter";
const emptyConLAE = {
  clase: "con-lae", tipo_documento: "J", documento: "", nombre: "", razon_social: "",
  denominacion_comercial: "", actividad_economica: "", area_total_establecimiento: "",
  email: "", telefono: "", celular: "", direccion_fiscal: "", sector: "",
  punto_referencia: "", cedula_representante_legal: "", nombre_representante_legal: "",
  celular_representante_legal: "", codigo_catastral: "", sin_codigo_catastral: false,
  documento_catastro_url: "", documento_catastro_nombre: "",
  rif_imagen_url: "", rif_imagen_nombre: "",
  cuenta_contrato_electricidad: "",
  ultimo_pago_mes: "Junio", ultimo_pago_anio: 2026, observaciones: "",
  estado_nombre: "Aragua", municipio_id: "", municipio_nombre: "José Félix Ribas", parroquia_id: "", parroquia_nombre: "",
  sedes: [], // Sucursales adicionales bajo el mismo RIF (multi-sede). Cada item: {id?, nombre_sede, area_total_establecimiento, direccion_fiscal, codigo_catastral, cuenta_contrato_electricidad, punto_referencia, estado_nombre, municipio_id, municipio_nombre, parroquia_id, parroquia_nombre, sector, ultimo_pago_mes, ultimo_pago_anio}
};
const emptySede = {
  nombre_sede: "", area_total_establecimiento: "", direccion_fiscal: "", codigo_catastral: "",
  cuenta_contrato_electricidad: "", punto_referencia: "",
  estado_nombre: "Aragua", municipio_id: "", municipio_nombre: "", parroquia_id: "", parroquia_nombre: "",
  sector: "", ultimo_pago_mes: "Junio", ultimo_pago_anio: 2026,
  documento_catastro_url: "", documento_catastro_nombre: "",
  actividad_economica: "",  // Cada sede puede tener su propia actividad (afecta tarifa aplicable)
};
const emptySinLAE = {
  clase: "sin-lae", tipo_documento: "V", documento: "", nombre: "", apellido: "",
  email: "", telefono: "", celular: "", direccion_fiscal: "", sector: "", observaciones: "",
  estado_nombre: "Aragua", municipio_id: "", municipio_nombre: "José Félix Ribas", parroquia_id: "", parroquia_nombre: "",
};
const emptyInm = { direccion:"", metros_cuadrados:0, tipo_zona:"residencial", codigo_clasificacion_aseo:"", actividad_especifica:"", sector:"", codigo_catastral:"" };

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];
const ANIOS = (() => {
  const actual = new Date().getFullYear();
  const arr = [];
  for (let y = actual + 1; y >= 2010; y--) arr.push(y);
  return arr;
})();

// ============ Editor de Sedes / Sucursales (multi-sede LAE) ============
function SedesEditor({ sedes, onChange, municipios, sectores = [], tarifas = [], auth, API, onSectorCreated }) {
  const [expanded, setExpanded] = useState({}); // { idx: bool }
  // Búsqueda de actividad económica por sede: { idx: "texto de búsqueda" }
  const [actSearchBySede, setActSearchBySede] = useState({});
  // Lista única de actividades del catálogo (una sola vez)
  const actividadesCatalogo = useMemo(
    () => Array.from(new Set((tarifas || []).map(t => t.actividad_especifica).filter(Boolean))).sort(),
    [tarifas],
  );
  const toggle = (i) => setExpanded(e => ({ ...e, [i]: !e[i] }));

  // Estado para el diálogo "Crear nuevo sector" dentro de cada sede
  const [newSectorOpenIdx, setNewSectorOpenIdx] = useState(null); // índice de sede activa o null
  const [newSectorName, setNewSectorName] = useState("");
  const [savingSector, setSavingSector] = useState(false);
  const saveNewSectorForSede = async () => {
    const nombre = String(newSectorName || "").trim();
    if (nombre.length < 3) { toast.error("El nombre debe tener al menos 3 caracteres"); return; }
    try {
      setSavingSector(true);
      const r = await axios.post(`${API}/definiciones/sectores`, { nombre }, auth);
      const nuevo = r.data.sector;
      if (onSectorCreated) onSectorCreated(nuevo);
      if (newSectorOpenIdx != null) updateSede(newSectorOpenIdx, { sector: nuevo.nombre });
      setNewSectorOpenIdx(null); setNewSectorName("");
      toast.success(`Sector "${nombre}" creado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el sector");
    } finally { setSavingSector(false); }
  };

  const addSede = () => {
    const nueva = { ...emptySede, nombre_sede: `Sucursal ${sedes.length + 1}` };
    const next = [ ...sedes, nueva ];
    onChange(next);
    setExpanded(e => ({ ...e, [next.length - 1]: true }));
  };
  const removeSede = (i) => {
    const s = sedes[i];
    const next = sedes.filter((_, idx) => idx !== i);
    // Si tiene id (sede existente en BD), notificar para eliminarla en el save
    onChange(next, s?.id || null);
  };
  const updateSede = (i, patch) => {
    const next = sedes.map((s, idx) => idx === i ? { ...s, ...patch } : s);
    onChange(next);
  };
  const uploadDocSede = async (i, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("El archivo supera 8 MB"); return; }
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/recaudaciones/comprobante/upload`, fd, { ...auth, headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
      updateSede(i, { documento_catastro_url: r.data.url, documento_catastro_nombre: file.name });
      toast.success("Documento cargado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el archivo");
    }
  };

  return (
    <div className="col-span-2 mt-4 border-t border-green-100 pt-4" data-testid="sedes-editor">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Building2 className="w-5 h-5 text-green-700" />
          <div>
            <h4 className="font-semibold text-green-900 text-sm">Sedes / Sucursales adicionales</h4>
            <p className="text-[11px] text-slate-500">Agrega múltiples sedes del mismo RIF con datos independientes (m², ubicación, códigos). Al liquidar aparecerán todas en la misma planilla consolidada.</p>
          </div>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={addSede}
          className="bg-orange-500 hover:bg-orange-600 text-white h-8"
          data-testid="btn-add-sede"
        >
          <Plus className="w-4 h-4 mr-1" />Agregar sede
        </Button>
      </div>

      {sedes.length === 0 && (
        <div className="text-center py-6 border border-dashed border-green-200 rounded-lg bg-green-50/40">
          <Building2 className="w-8 h-8 text-green-300 mx-auto mb-1" />
          <p className="text-xs text-slate-500">Sin sedes adicionales. La dirección principal ya se toma del formulario. Si el contribuyente tiene más sucursales bajo el mismo RIF, agrégalas aquí.</p>
        </div>
      )}

      <div className="space-y-2">
        {sedes.map((s, i) => {
          const isNew = !s.id;
          const isOpen = !!expanded[i];
          return (
            <div key={s.id || `new-${i}`} className="border border-green-200 rounded-lg overflow-hidden" data-testid={`sede-card-${i}`}>
              <div className="flex items-center gap-2 px-3 py-2 bg-green-50/50 hover:bg-green-50 cursor-pointer" onClick={() => toggle(i)}>
                <Building2 className="w-4 h-4 text-green-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-green-900 text-sm truncate">{s.nombre_sede || `Sede ${i + 1}`}</span>
                    {isNew && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 font-bold">NUEVA</span>}
                    {s.area_total_establecimiento ? <span className="text-[10px] text-slate-600">· {s.area_total_establecimiento} m²</span> : null}
                  </div>
                  {(s.direccion_fiscal || s.direccion) && <div className="text-[11px] text-slate-500 truncate">{s.direccion_fiscal || s.direccion}</div>}
                </div>
                <button type="button" onClick={(e) => { e.stopPropagation(); removeSede(i); }} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Eliminar sede" data-testid={`btn-remove-sede-${i}`}>
                  <X className="w-4 h-4" />
                </button>
                {isOpen ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
              </div>
              {isOpen && (
                <div className="p-3 grid grid-cols-1 md:grid-cols-2 gap-3 bg-white">
                  <div>
                    <Label className="text-xs">Nombre de la sede</Label>
                    <Input value={s.nombre_sede || ""} onChange={e => updateSede(i, { nombre_sede: e.target.value })} placeholder="Ej: Matriz, Sucursal Centro" data-testid={`sede-${i}-nombre`} />
                  </div>
                  <div>
                    <Label className="text-xs">Área total establecimiento (m²) <span className="text-red-500">*</span></Label>
                    <Input type="number" min="0" step="0.01" value={s.area_total_establecimiento || ""} onChange={e => updateSede(i, { area_total_establecimiento: e.target.value })} placeholder="0" data-testid={`sede-${i}-area`} />
                  </div>
                  {/* Actividad económica de la sede (puede diferir de la sede matriz) */}
                  <div className="md:col-span-2">
                    <Label className="text-xs">Actividad económica de la sede <span className="text-red-500">*</span></Label>
                    <Select
                      value={s.actividad_economica || ""}
                      onValueChange={v => updateSede(i, { actividad_economica: v })}
                    >
                      <SelectTrigger data-testid={`sede-${i}-actividad`}>
                        <SelectValue placeholder="Seleccione actividad económica" />
                      </SelectTrigger>
                      <SelectContent className="max-h-72">
                        <div className="sticky top-0 bg-white p-2 border-b border-green-100 z-10">
                          <div className="flex items-center gap-2 bg-green-50 rounded-md px-2">
                            <Search className="w-3.5 h-3.5 text-green-700 shrink-0" />
                            <Input
                              data-testid={`sede-${i}-actividad-search`}
                              value={actSearchBySede[i] || ""}
                              onChange={e => setActSearchBySede(prev => ({ ...prev, [i]: e.target.value }))}
                              onKeyDown={e => e.stopPropagation()}
                              placeholder="Buscar actividad…"
                              className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-7 text-sm"
                            />
                          </div>
                        </div>
                        {(() => {
                          const q = (actSearchBySede[i] || "").toLowerCase().trim();
                          const legacy = s.actividad_economica && !actividadesCatalogo.some(a => a.toUpperCase() === s.actividad_economica.toUpperCase())
                            ? s.actividad_economica
                            : null;
                          const filtradas = q
                            ? actividadesCatalogo.filter(a => a.toLowerCase().includes(q))
                            : actividadesCatalogo;
                          if (actividadesCatalogo.length === 0 && !legacy) {
                            return <SelectItem disabled value="_empty">Sin tarifas cargadas. Registre en Aseo Urbano.</SelectItem>;
                          }
                          if (filtradas.length === 0 && !legacy) {
                            return <SelectItem disabled value="_nores">Sin coincidencias</SelectItem>;
                          }
                          return (
                            <>
                              {legacy && (
                                <SelectItem key={`legacy-${legacy}`} value={legacy} className="text-amber-700 bg-amber-50 focus:bg-amber-100">
                                  {legacy} <span className="text-xs">(legacy — no en catálogo)</span>
                                </SelectItem>
                              )}
                              {filtradas.map(act => <SelectItem key={act} value={act}>{act}</SelectItem>)}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-slate-500 mt-1">Determina la tarifa aplicable de esta sede al liquidar.</p>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs">Domicilio fiscal de la sede <span className="text-red-500">*</span></Label>
                    <Input value={s.direccion_fiscal || s.direccion || ""} onChange={e => updateSede(i, { direccion_fiscal: e.target.value })} placeholder="Av / Calle / Sector / Local" data-testid={`sede-${i}-direccion`} />
                  </div>
                  <div>
                    <Label className="text-xs">Punto de referencia</Label>
                    <Input value={s.punto_referencia || ""} onChange={e => updateSede(i, { punto_referencia: e.target.value })} placeholder="Cerca de..." data-testid={`sede-${i}-referencia`} />
                  </div>
                  <div>
                    <Label className="text-xs">Sector</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SectorCombobox
                          value={s.sector || ""}
                          onChange={(v) => updateSede(i, { sector: v })}
                          sectores={sectores}
                          emptyText="No hay sectores cargados"
                          testId={`sede-${i}-sector`}
                        />
                      </div>
                      <Button
                        type="button"
                        size="icon"
                        variant="outline"
                        onClick={() => { setNewSectorOpenIdx(i); setNewSectorName(""); }}
                        title="Crear nuevo sector"
                        data-testid={`sede-${i}-btn-nuevo-sector`}
                        className="border-green-200 text-green-800 hover:bg-green-50 shrink-0"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  <div className="md:col-span-2 mt-1 pt-2 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-green-800 mb-1">Ubicación geográfica</p>
                  </div>
                  <div>
                    <Label className="text-xs">Estado</Label>
                    <Input value={s.estado_nombre || ""} onChange={e => updateSede(i, { estado_nombre: e.target.value })} placeholder="Estado" data-testid={`sede-${i}-estado`} />
                  </div>
                  <div>
                    <Label className="text-xs">Municipio</Label>
                    <Select value={s.municipio_id || ""} onValueChange={v => {
                      const m = municipios.find(x => x.id === v);
                      updateSede(i, { municipio_id: v, municipio_nombre: m?.nombre || "", parroquia_id: "", parroquia_nombre: "" });
                    }}>
                      <SelectTrigger data-testid={`sede-${i}-municipio`}><SelectValue placeholder="Seleccionar municipio" /></SelectTrigger>
                      <SelectContent>
                        {(municipios || []).filter(m => !s.estado_nombre || m.estado_nombre === s.estado_nombre).map(m => (
                          <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Parroquia</Label>
                    {(() => {
                      const mSel = (municipios || []).find(m => m.id === s.municipio_id);
                      const parroquias = mSel && Array.isArray(mSel.parroquias) ? mSel.parroquias : [];
                      return (
                        <Select
                          value={s.parroquia_id || ""}
                          disabled={!s.municipio_id}
                          onValueChange={v => {
                            const p = parroquias.find(x => x.id === v);
                            updateSede(i, { parroquia_id: v, parroquia_nombre: p?.nombre || "" });
                          }}
                        >
                          <SelectTrigger data-testid={`sede-${i}-parroquia`}>
                            <SelectValue placeholder={s.municipio_id ? "Seleccionar parroquia" : "Primero elija municipio"} />
                          </SelectTrigger>
                          <SelectContent>
                            {parroquias.length === 0
                              ? <SelectItem disabled value="_empty">Este municipio no tiene parroquias</SelectItem>
                              : parroquias.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      );
                    })()}
                  </div>

                  <div className="md:col-span-2 mt-1 pt-2 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-green-800 mb-1">Códigos de identificación</p>
                  </div>
                  <div>
                    <Label className="text-xs">Código catastral</Label>
                    <CatastralInput
                      value={s.codigo_catastral || ""}
                      onChange={v => updateSede(i, { codigo_catastral: v })}
                      testId={`sede-${i}-catastral`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Cuenta contrato electricidad</Label>
                    <Input value={s.cuenta_contrato_electricidad || ""} onChange={e => updateSede(i, { cuenta_contrato_electricidad: e.target.value })} placeholder="N° cuenta CORPOELEC" data-testid={`sede-${i}-electricidad`} />
                  </div>

                  <div className="md:col-span-2">
                    <Label className="text-xs">Contrato de arrendamiento ó Ficha o Cédula Catastral</Label>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,image/jpeg,application/pdf"
                        onChange={(e) => { const f = e.target.files?.[0]; uploadDocSede(i, f); e.target.value = ""; }}
                        className="text-xs file:mr-2 file:py-1 file:px-2.5 file:rounded-md file:border-0 file:bg-green-600 file:text-white file:cursor-pointer hover:file:bg-green-700"
                        data-testid={`sede-${i}-doc-catastro`}
                      />
                      {s.documento_catastro_url && (
                        <div className="flex items-center gap-2 text-xs">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${s.documento_catastro_url}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-medium"
                            title={s.documento_catastro_nombre || "Ver documento"}
                          >
                            <FileText className="w-3.5 h-3.5" /> {s.documento_catastro_nombre ? (s.documento_catastro_nombre.length > 24 ? s.documento_catastro_nombre.slice(0, 21) + "…" : s.documento_catastro_nombre) : "Ver"}
                          </a>
                          <button type="button" onClick={() => updateSede(i, { documento_catastro_url: "", documento_catastro_nombre: "" })} className="text-red-500 hover:text-red-700" title="Quitar documento">✕</button>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">PDF o JPG · máx. 8 MB</p>
                  </div>

                  <div className="md:col-span-2 mt-1 pt-2 border-t border-slate-100">
                    <p className="text-[11px] font-semibold text-green-800 mb-1">Último mes de pago del aseo</p>
                  </div>
                  <div>
                    <Label className="text-xs">Mes</Label>
                    <Select value={s.ultimo_pago_mes || ""} onValueChange={v => updateSede(i, { ultimo_pago_mes: v })}>
                      <SelectTrigger data-testid={`sede-${i}-pago-mes`}><SelectValue placeholder="Mes" /></SelectTrigger>
                      <SelectContent>{MESES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Año</Label>
                    <Select value={String(s.ultimo_pago_anio || "")} onValueChange={v => updateSede(i, { ultimo_pago_anio: Number(v) })}>
                      <SelectTrigger data-testid={`sede-${i}-pago-anio`}><SelectValue placeholder="Año" /></SelectTrigger>
                      <SelectContent>{ANIOS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Diálogo compartido para crear un nuevo sector desde cualquier sede */}
      <Dialog open={newSectorOpenIdx != null} onOpenChange={(o) => { if (!o) { setNewSectorOpenIdx(null); setNewSectorName(""); } }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-green-900">Crear nuevo sector</DialogTitle>
            <DialogDescription>El sector quedará disponible en toda la plataforma.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label>Nombre del sector*</Label>
            <Input
              data-testid="sede-input-nuevo-sector"
              value={newSectorName}
              onChange={e => setNewSectorName(e.target.value)}
              placeholder="Ej. José Félix Ribas"
              autoFocus
              onKeyDown={e => { if (e.key === "Enter") saveNewSectorForSede(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNewSectorOpenIdx(null); setNewSectorName(""); }} disabled={savingSector}>Cancelar</Button>
            <Button data-testid="sede-btn-guardar-sector" onClick={saveNewSectorForSede} disabled={savingSector} className="bg-green-700 hover:bg-green-800 text-white">
              {savingSector ? "Guardando..." : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function ContribuyentesPage() {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [tab, setTab] = useState("con-lae");
  const [stats, setStats] = useState({ total: 0, con_lae: { total: 0, activos: 0, suspendidos: 0 }, sin_lae: { total: 0, activos: 0, suspendidos: 0 } });
  const [statsRefresh, setStatsRefresh] = useState(0);

  useEffect(() => {
    axios.get(`${API}/contribuyentes/stats`, auth)
      .then(r => setStats(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statsRefresh]);

  const bumpStats = () => setStatsRefresh(x => x + 1);
  const nfmt = (n) => Number(n || 0).toLocaleString("es-VE");

  return (
    <div className="space-y-6" data-testid="contribuyentes-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900">Contribuyentes</h1>
          <p className="text-green-700/70 mt-1">Gestión de contribuyentes con y sin Licencia de Actividad Económica (LAE)</p>
        </div>
        {/* KPI counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
          <div className="rounded-lg border border-green-100 bg-green-50/60 px-3 py-2 text-center min-w-[110px]" data-testid="kpi-total">
            <p className="text-[10px] uppercase font-medium text-green-700/70 tracking-wide">Total</p>
            <p className="text-xl font-bold text-green-900 leading-tight">{nfmt(stats.total)}</p>
          </div>
          <div className="rounded-lg border border-emerald-100 bg-emerald-50/60 px-3 py-2 text-center min-w-[110px]" data-testid="kpi-con-lae">
            <p className="text-[10px] uppercase font-medium text-emerald-700/70 tracking-wide">Con LAE</p>
            <p className="text-xl font-bold text-emerald-800 leading-tight">{nfmt(stats.con_lae.total)}</p>
            <p className="text-[10px] text-emerald-700/60">{nfmt(stats.con_lae.activos)} activos · {nfmt(stats.con_lae.suspendidos)} susp.</p>
          </div>
          <div className="rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2 text-center min-w-[110px]" data-testid="kpi-sin-lae">
            <p className="text-[10px] uppercase font-medium text-sky-700/70 tracking-wide">Sin LAE</p>
            <p className="text-xl font-bold text-sky-800 leading-tight">{nfmt(stats.sin_lae.total)}</p>
            <p className="text-[10px] text-sky-700/60">{nfmt(stats.sin_lae.activos)} activos · {nfmt(stats.sin_lae.suspendidos)} susp.</p>
          </div>
          <div className="rounded-lg border border-orange-100 bg-orange-50/60 px-3 py-2 text-center min-w-[110px]" data-testid="kpi-suspendidos">
            <p className="text-[10px] uppercase font-medium text-orange-700/70 tracking-wide">Suspendidos</p>
            <p className="text-xl font-bold text-orange-700 leading-tight">{nfmt(stats.con_lae.suspendidos + stats.sin_lae.suspendidos)}</p>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-green-50 border border-green-100">
          <TabsTrigger value="con-lae" data-testid="tab-con-lae">
            <Briefcase className="w-4 h-4 mr-2" />Con LAE (Comerciantes)
            <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold" data-testid="tab-badge-con-lae">{nfmt(stats.con_lae.total)}</span>
          </TabsTrigger>
          <TabsTrigger value="pre-aprobado" data-testid="tab-pre-aprobado">
            <AlertCircle className="w-4 h-4 mr-2" />Con LAE (Pre-Aprobado)
            {stats.pre_aprobados > 0 && (
              <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full bg-amber-500 text-white text-[10px] font-bold animate-pulse" data-testid="tab-badge-pre-aprobado">{nfmt(stats.pre_aprobados)}</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="sin-lae" data-testid="tab-sin-lae">
            <Users className="w-4 h-4 mr-2" />Sin LAE (Residencial)
            <span className="ml-2 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded-full bg-sky-600 text-white text-[10px] font-bold" data-testid="tab-badge-sin-lae">{nfmt(stats.sin_lae.total)}</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="con-lae" className="mt-4">
          <ListaContribuyentes auth={auth} API={API} clase="con-lae" onDataChange={bumpStats} />
        </TabsContent>
        <TabsContent value="pre-aprobado" className="mt-4">
          <ListaPreAprobados auth={auth} API={API} onDataChange={bumpStats} />
        </TabsContent>
        <TabsContent value="sin-lae" className="mt-4">
          <ListaContribuyentes auth={auth} API={API} clase="sin-lae" onDataChange={bumpStats} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Cache del logo Palma Plus (base64) para insertarlo en pies de página PDF
function contribuyenteToRow(c, isConLAE) {
  if (isConLAE) {
    return {
      "RIF": `${c.tipo_documento || ""}-${c.documento || ""}`,
      "Razón Social": c.razon_social || c.nombre || "",
      "Denominación Comercial": c.denominacion_comercial || "",
      "Actividad Económica": c.actividad_economica || "",
      "Área Establecimiento (m²)": Number(c.area_total_establecimiento || 0),
      "Domicilio Fiscal": c.direccion_fiscal || "",
      "Sector": c.sector || "",
      "Estado": c.estado_nombre || "",
      "Municipio": c.municipio_nombre || "",
      "Parroquia": c.parroquia_nombre || "",
      "Email": c.email || "",
      "Teléfono": c.telefono || "",
      "Celular": c.celular || "",
      "Representante Legal": c.nombre_representante_legal || "",
      "Cédula Representante": c.cedula_representante_legal || "",
      "Celular Representante": c.celular_representante_legal || "",
      "Código Catastral": c.codigo_catastral || "",
      "Cuenta Contrato Electricidad": c.cuenta_contrato_electricidad || "",
      "Estatus": c.is_active ? "Activo" : "Suspendido",
      "Fecha Registro": (c.created_at || "").slice(0, 10),
    };
  }
  return {
    "Documento": `${c.tipo_documento || ""}-${c.documento || ""}`,
    "Nombre": c.nombre || "",
    "Apellido": c.apellido || "",
    "Email": c.email || "",
    "Teléfono": c.telefono || "",
    "Celular": c.celular || "",
    "Domicilio": c.direccion_fiscal || "",
    "Sector": c.sector || "",
    "Observaciones": c.observaciones || "",
    "Estatus": c.is_active ? "Activo" : "Suspendido",
    "Fecha Registro": (c.created_at || "").slice(0, 10),
  };
}


// ================== Componente: Detalle en modo lectura de un contribuyente con LAE ==================
const VerField = ({ label, value, mono = false, wide = false }) => {
  const display = (value === null || value === undefined || value === "") ? "—" : value;
  return (
    <div className={wide ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{label}</div>
      <div className={`text-sm text-slate-900 mt-0.5 ${mono ? "font-mono" : ""}`}>{display}</div>
    </div>
  );
};

const VerSection = ({ title, color = "green", children }) => {
  const paletteMap = {
    green:   "border-green-100 bg-green-50/40 text-green-800",
    sky:     "border-sky-100 bg-sky-50/40 text-sky-800",
    orange:  "border-orange-100 bg-orange-50/40 text-orange-800",
    emerald: "border-emerald-100 bg-emerald-50/40 text-emerald-800",
    slate:   "border-slate-200 bg-slate-50/40 text-slate-800",
    amber:   "border-amber-100 bg-amber-50/40 text-amber-800",
  };
  const palette = paletteMap[color] || paletteMap.green;
  return (
    <div className={`rounded-xl border p-4 mt-3 ${palette.split(" text-")[0]}`}>
      <h4 className={`text-xs font-extrabold uppercase tracking-wider mb-3 text-${color}-800`}>{title}</h4>
      <div className="grid grid-cols-2 gap-x-4 gap-y-3">{children}</div>
    </div>
  );
};

function VerContribuyenteDetalle({ c }) {
  const { token, API } = useAuth();
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [sedes, setSedes] = useState([]);
  const fmtNum = (v) => (v ? Number(v).toLocaleString("es-VE") : "—");
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("es-VE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "America/Caracas" }); }
    catch { return iso; }
  };

  useEffect(() => {
    if (!c?.id) return;
    axios.get(`${API}/contribuyentes/${c.id}/sedes`, auth)
      .then(r => setSedes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setSedes([]));
    // eslint-disable-next-line
  }, [c?.id]);

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-start justify-between p-4 rounded-xl bg-gradient-to-r from-emerald-50 to-orange-50 border border-emerald-100">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold flex items-center gap-1"><Briefcase className="w-3 h-3" /> Con LAE · Comerciante</div>
          <div className="font-display font-extrabold text-xl text-green-900 mt-1">{c.razon_social || c.nombre || "—"}</div>
          {c.denominacion_comercial && <div className="text-sm text-slate-700 italic">&ldquo;{c.denominacion_comercial}&rdquo;</div>}
          <div className="text-xs text-slate-600 font-mono mt-1">RIF: {c.tipo_documento}-{c.documento}</div>
        </div>
        <div className="text-right">
          {c.is_active
            ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Activo</Badge>
            : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Suspendido</Badge>}
          <div className="text-[10px] text-slate-500 mt-1">Registrado: {fmtDate(c.created_at)}</div>
        </div>
      </div>

      <VerSection title="Datos fiscales">
        <VerField label="Actividad económica" value={c.actividad_economica} wide />
        <VerField label="Área establecimiento" value={c.area_total_establecimiento ? `${fmtNum(c.area_total_establecimiento)} m²` : "—"} />
        <VerField label="Código catastral" value={c.codigo_catastral} mono />
        <VerField label="Domicilio fiscal" value={c.direccion_fiscal} wide />
        <VerField label="Punto de referencia" value={c.punto_referencia} wide />
        <VerField label="Cuenta Contrato Electricidad" value={c.cuenta_contrato_electricidad} mono />
      </VerSection>

      <VerSection title="Ubicación" color="sky">
        <VerField label="Estado" value={c.estado_nombre} />
        <VerField label="Municipio" value={c.municipio_nombre} />
        <VerField label="Parroquia" value={c.parroquia_nombre} />
        <VerField label="Sector" value={c.sector} />
      </VerSection>

      <VerSection title="Contacto" color="orange">
        <VerField label="Email" value={c.email} />
        <VerField label="Teléfono" value={c.telefono} mono />
        <VerField label="Celular" value={c.celular} mono />
      </VerSection>

      <VerSection title="Representante legal" color="emerald">
        <VerField label="Nombre" value={c.nombre_representante_legal} />
        <VerField label="Cédula" value={c.cedula_representante_legal} mono />
        <VerField label="Celular" value={c.celular_representante_legal} mono />
      </VerSection>

      {(c.rif_imagen_url || c.documento_catastro_url) && (
        <VerSection title="Documentos adjuntos" color="slate">
          {c.rif_imagen_url && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">RIF (imagen)</div>
              <a href={c.rif_imagen_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1 mt-0.5">
                <FileText className="w-4 h-4" /> {c.rif_imagen_nombre || "Ver archivo"}
              </a>
            </div>
          )}
          {c.documento_catastro_url && (
            <div>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Documento de catastro</div>
              <a href={c.documento_catastro_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-700 hover:underline inline-flex items-center gap-1 mt-0.5">
                <FileText className="w-4 h-4" /> {c.documento_catastro_nombre || "Ver archivo"}
              </a>
            </div>
          )}
        </VerSection>
      )}

      {sedes.length > 0 && (
        <VerSection title={`Sedes / Sucursales (${sedes.length})`} color="emerald">
          <div className="col-span-2 space-y-2">
            {sedes.map((s, i) => (
              <div key={s.id} className="border border-emerald-200 rounded-lg p-3 bg-emerald-50/40" data-testid={`ver-sede-${i}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-700" />
                    <span className="font-semibold text-green-900 text-sm">{s.nombre_sede || `Sede ${i+1}`}</span>
                  </div>
                  {s.metros_cuadrados ? <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 font-bold">{fmtNum(s.metros_cuadrados)} m²</span> : null}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {s.direccion && <div className="col-span-2"><span className="text-slate-500">Domicilio:</span> <span className="text-slate-800">{s.direccion}</span></div>}
                  {s.punto_referencia && <div className="col-span-2"><span className="text-slate-500">Referencia:</span> <span className="text-slate-800">{s.punto_referencia}</span></div>}
                  {s.municipio_nombre && <div><span className="text-slate-500">Municipio:</span> <span className="text-slate-800">{s.municipio_nombre}</span></div>}
                  {s.parroquia_nombre && <div><span className="text-slate-500">Parroquia:</span> <span className="text-slate-800">{s.parroquia_nombre}</span></div>}
                  {s.sector && <div><span className="text-slate-500">Sector:</span> <span className="text-slate-800">{s.sector}</span></div>}
                  {s.codigo_catastral && <div className="font-mono"><span className="text-slate-500">Catastro:</span> <span className="text-slate-800">{s.codigo_catastral}</span></div>}
                  {s.cuenta_contrato_electricidad && <div className="font-mono"><span className="text-slate-500">Elect.:</span> <span className="text-slate-800">{s.cuenta_contrato_electricidad}</span></div>}
                  {(s.ultimo_pago_mes || s.ultimo_pago_anio) && <div><span className="text-slate-500">Último pago:</span> <span className="text-slate-800">{[s.ultimo_pago_mes, s.ultimo_pago_anio].filter(Boolean).join(" ")}</span></div>}
                  {s.documento_catastro_url && (
                    <div className="col-span-2">
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${s.documento_catastro_url}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-blue-700 hover:underline text-xs" data-testid={`ver-sede-${i}-doc`}>
                        <FileText className="w-3.5 h-3.5" /> {s.documento_catastro_nombre || "Contrato / Ficha catastral"}
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </VerSection>
      )}

      {(c.ultimo_pago_mes || c.ultimo_pago_anio) && (
        <VerSection title="Último pago registrado" color="amber">
          <VerField label="Mes" value={c.ultimo_pago_mes} />
          <VerField label="Año" value={c.ultimo_pago_anio} />
        </VerSection>
      )}

      {c.observaciones && (
        <VerSection title="Observaciones" color="slate">
          <div className="col-span-2 text-sm text-slate-800 whitespace-pre-wrap">{c.observaciones}</div>
        </VerSection>
      )}
    </div>
  );
}


function exportarContribuyentes({ formato, rows, isConLAE }) {
  if (!rows.length) { toast.error("Nada que exportar con los filtros actuales"); return; }
  const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  const contexto = isConLAE ? "con-lae" : "sin-lae";
  const filename = `contribuyentes_${contexto}_${stamp}`;

  if (formato === "excel") {
    const data = rows.map(c => contribuyenteToRow(c, isConLAE));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map(k => ({ wch: Math.max(k.length + 2, 16) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, isConLAE ? "Con LAE" : "Sin LAE");
    XLSX.writeFile(wb, `${filename}.xlsx`);
    toast.success(`Excel descargado (${rows.length} contribuyentes)`);
  } else if (formato === "json") {
    const blob = new Blob([JSON.stringify(rows, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `${filename}.json`; a.click(); URL.revokeObjectURL(url);
    toast.success(`JSON descargado (${rows.length} contribuyentes)`);
  } else if (formato === "pdf") {
    loadPalmaLogo().then(logoDataURL => {
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      const { fecha, hora } = fechaHoraVE();

      doc.setFontSize(16); doc.setTextColor(20, 83, 45);
      doc.text(`SISAUR · Contribuyentes ${isConLAE ? "con LAE (Comerciantes)" : "sin LAE (Residenciales)"}`, 40, 40);
      doc.setFontSize(11); doc.setTextColor(80);
      doc.text(`Generado: ${fecha} ${hora} · Total: ${rows.length} contribuyentes`, 40, 58);
      const activos = rows.filter(c => c.is_active).length;
      doc.text(`Activos: ${activos} · Suspendidos: ${rows.length - activos}`, 40, 74);

      const head = isConLAE
        ? [["RIF", "Razón Social", "Denominación", "Domicilio Fiscal", "Sector", "Estatus"]]
        : [["Documento", "Nombre", "Email", "Teléfono", "Sector", "Estatus"]];
      const body = rows.map(c => isConLAE ? [
        `${c.tipo_documento || ""}-${c.documento || ""}`,
        (c.razon_social || c.nombre || "—").slice(0, 40),
        (c.denominacion_comercial || "—").slice(0, 30),
        (c.direccion_fiscal || "—").slice(0, 50),
        c.sector || "—",
        c.is_active ? "Activo" : "Suspendido",
      ] : [
        `${c.tipo_documento || ""}-${c.documento || ""}`,
        `${c.nombre || ""} ${c.apellido || ""}`.trim() || "—",
        (c.email || "—").slice(0, 30),
        c.telefono || c.celular || "—",
        c.sector || "—",
        c.is_active ? "Activo" : "Suspendido",
      ]);
      autoTable(doc, {
        head, body, startY: 90,
        margin: { bottom: 60 },
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [20, 83, 45], textColor: 255 },
        alternateRowStyles: { fillColor: [240, 253, 244] },
        didDrawPage: (data) => drawPalmaFooter(doc, { logoDataURL, fecha, hora, pageNumber: data.pageNumber }),
      });
      doc.save(`${filename}.pdf`);
      toast.success(`PDF descargado (${rows.length} contribuyentes)`);
    });
  }
}

function ListaContribuyentes({ auth, API, clase, onDataChange }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  // Multi-filtros por campo (solo Con LAE)
  const [filters, setFilters] = useState({ rif: "", razon_social: "", denominacion_comercial: "", direccion_fiscal: "", is_active: "todos" });
  // Paginación server-side
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const empty = clase === "con-lae" ? emptyConLAE : emptySinLAE;
  const [form, setForm] = useState(empty);
  // Chequeo de RIF único en tiempo real. status: "idle" | "checking" | "available" | "taken"
  const [rifCheck, setRifCheck] = useState({ status: "idle", contribuyente: null });
  const [detail, setDetail] = useState(null);
  const [viewing, setViewing] = useState(null); // Contribuyente seleccionado para "Ver" (solo lectura)
  const [inmuebles, setInmuebles] = useState([]);
  const [tarifas, setTarifas] = useState([]);
  const [municipios, setMunicipios] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [actividadSearch, setActividadSearch] = useState("");
  const [inmForm, setInmForm] = useState(emptyInm);
  const [inmOpen, setInmOpen] = useState(false);
  // Diálogo inline para crear un nuevo sector desde el propio formulario del contribuyente
  const [newSectorOpen, setNewSectorOpen] = useState(false);
  const [newSectorName, setNewSectorName] = useState("");
  const [savingSector, setSavingSector] = useState(false);
  const saveNewSector = async () => {
    const nombre = newSectorName.trim();
    if (!nombre) { toast.error("Ingrese un nombre de sector"); return; }
    try {
      setSavingSector(true);
      await axios.post(`${API}/definiciones/sectores`, { nombre }, auth);
      const r = await axios.get(`${API}/definiciones/sectores`, auth);
      setSectores(r.data);
      setForm(f => ({ ...f, sector: nombre })); // seleccionar el recién creado
      setNewSectorName("");
      setNewSectorOpen(false);
      toast.success(`Sector "${nombre}" creado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo crear el sector");
    } finally { setSavingSector(false); }
  };

  const fetchAllForExport = async () => {
    // Trae TODOS los contribuyentes que matcheen los filtros (sin paginación)
    const params = { clase, limit: 5000 };
    if (clase === "con-lae") {
      if (filters.rif.trim()) params.rif = filters.rif.trim();
      if (filters.razon_social.trim()) params.razon_social = filters.razon_social.trim();
      if (filters.denominacion_comercial.trim()) params.denominacion_comercial = filters.denominacion_comercial.trim();
      if (filters.direccion_fiscal.trim()) params.direccion_fiscal = filters.direccion_fiscal.trim();
      if (filters.is_active === "activos") params.is_active = true;
      if (filters.is_active === "suspendidos") params.is_active = false;
    } else if (search.trim()) {
      params.search = search.trim();
    }
    try {
      const r = await axios.get(`${API}/contribuyentes`, { ...auth, params });
      return Array.isArray(r.data) ? r.data : (r.data.items || []);
    } catch { return []; }
  };
  const doExport = async (formato) => {
    const rows = await fetchAllForExport();
    if (!rows.length) { toast.error("Nada que exportar con los filtros actuales"); return; }
    exportarContribuyentes({ formato, rows, isConLAE });
  };

  const load = () => {
    const params = { clase, paginated: true, page, page_size: pageSize, sort, order };
    if (clase === "con-lae") {
      if (filters.rif.trim()) params.rif = filters.rif.trim();
      if (filters.razon_social.trim()) params.razon_social = filters.razon_social.trim();
      if (filters.denominacion_comercial.trim()) params.denominacion_comercial = filters.denominacion_comercial.trim();
      if (filters.direccion_fiscal.trim()) params.direccion_fiscal = filters.direccion_fiscal.trim();
      if (filters.is_active === "activos") params.is_active = true;
      if (filters.is_active === "suspendidos") params.is_active = false;
    } else if (search.trim()) {
      params.search = search.trim();
    }
    setLoading(true);
    return axios.get(`${API}/contribuyentes`, { ...auth, params })
      .then(r => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.total_pages || 0);
      })
      .catch(() => { setItems([]); setTotal(0); setTotalPages(0); })
      .finally(() => setLoading(false));
  };
  // Debounce ligero para no saturar la API mientras el usuario escribe
  useEffect(() => {
    const t = setTimeout(() => { load(); setForm(empty); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, clase, filters.rif, filters.razon_social, filters.denominacion_comercial, filters.direccion_fiscal, filters.is_active, page, pageSize, sort, order]);
  // Reset a página 1 cuando cambian los filtros
  useEffect(() => {
    setPage(1);
    // eslint-disable-next-line
  }, [search, clase, filters.rif, filters.razon_social, filters.denominacion_comercial, filters.direccion_fiscal, filters.is_active, pageSize]);
  useEffect(() => { axios.get(`${API}/aseo-urbano/tarifas`, auth).then(r => setTarifas(r.data)); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    // Municipios y sectores son necesarios en ambos flujos (con-lae y sin-lae) para
    // los dropdowns de Estado/Municipio/Parroquia/Sector del formulario.
    axios.get(`${API}/definiciones/municipios`, auth).then(r => setMunicipios(r.data)).catch(() => {});
    axios.get(`${API}/definiciones/sectores`, auth).then(r => setSectores(r.data)).catch(() => {});
    /* eslint-disable-next-line */
  }, [clase]);

  // Listas en cascada (memorizadas)
  const estadosUnicos = Array.from(new Set(municipios.map(m => m.estado_nombre).filter(Boolean))).sort();
  const municipiosDelEstado = form.estado_nombre
    ? municipios.filter(m => m.estado_nombre === form.estado_nombre)
    : [];
  const parroquiasDelMunicipio = (() => {
    const m = municipios.find(x => x.id === form.municipio_id);
    return m && Array.isArray(m.parroquias) ? m.parroquias : [];
  })();

  // Auto-resolver municipio_id "José Félix Ribas" para ambos flujos (con-lae y sin-lae).
  // - Al crear (no editing): siempre lo aplica si el usuario no cambió el municipio.
  // - Al editar: solo si el contribuyente NO tiene municipio_id (registros antiguos sin municipio).
  // Se busca case-insensitive y con/sin acento.
  useEffect(() => {
    if (!municipios.length) return;
    if (form.municipio_id) return;
    const norm = s => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const target = municipios.find(m =>
      norm(m.estado_nombre) === "aragua" && norm(m.nombre).includes("jose felix ribas")
    );
    if (target) {
      setForm(f => ({
        ...f,
        estado_nombre: target.estado_nombre || f.estado_nombre || "Aragua",
        municipio_id: target.id,
        municipio_nombre: target.nombre,
      }));
    }
    /* eslint-disable-next-line */
  }, [municipios, editing, open, clase]);

  // === Chequeo de RIF único en tiempo real ===
  // Cuando el usuario escribe/cambia tipo_documento o documento, consulta al backend
  // para saber si el RIF ya está registrado a nombre de otro contribuyente.
  useEffect(() => {
    if (!open) return;
    const tipo = (form.tipo_documento || "").trim();
    const docRaw = (form.documento || "").replace(/[^A-Za-z0-9]/g, "").trim();
    if (!tipo || !docRaw || docRaw.length < 4) {
      setRifCheck({ status: "idle", contribuyente: null });
      return;
    }
    setRifCheck({ status: "checking", contribuyente: null });
    const t = setTimeout(async () => {
      try {
        const params = { tipo, documento: docRaw };
        if (editing?.id) params.exclude_id = editing.id;
        const r = await axios.get(`${API}/contribuyentes/check-rif`, { ...auth, params });
        if (r.data?.disponible) {
          setRifCheck({ status: "available", contribuyente: null });
        } else {
          setRifCheck({ status: "taken", contribuyente: r.data?.contribuyente || null });
        }
      } catch {
        setRifCheck({ status: "idle", contribuyente: null });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [form.tipo_documento, form.documento, open, editing?.id, auth]);

  const save = async () => {
    try {
      const payload = { ...form, clase };
      // === Validación de campos obligatorios (solo Con LAE) ===
      if (clase === "con-lae") {
        const camposReq = [
          ["tipo_documento",             "Tipo Doc."],
          ["documento",                  "RIF"],
          ["nombre",                     "Razón Social / Nombre"],
          ["denominacion_comercial",     "Denominación Comercial"],
          ["actividad_economica",        "Actividad Económica"],
          ["area_total_establecimiento", "Área total establecimiento (m²)"],
          ["ultimo_pago_mes",            "Último Mes de Pago del Aseo (Mes)"],
          ["ultimo_pago_anio",           "Último Mes de Pago del Aseo (Año)"],
          ["celular",                    "Celular"],
          ["estado_nombre",              "Estado"],
          ["municipio_nombre",           "Municipio"],
          ["parroquia_nombre",           "Parroquia"],
          ["sector",                     "Sector"],
          ["direccion_fiscal",           "Domicilio Fiscal"],
        ];
        const faltantes = camposReq
          .filter(([k]) => payload[k] === undefined || payload[k] === null || String(payload[k]).trim() === "")
          .map(([, label]) => label);
        // Validación adicional: área numérica > 0
        const areaNum = Number(payload.area_total_establecimiento);
        if (payload.area_total_establecimiento !== "" && payload.area_total_establecimiento != null && (!Number.isFinite(areaNum) || areaNum <= 0)) {
          faltantes.push("Área total establecimiento debe ser mayor a 0");
        }
        if (faltantes.length) {
          toast.error(`Faltan campos obligatorios: ${faltantes.join(" · ")}`);
          return;
        }
      }
      // === Sanitización: campos numéricos opcionales no pueden ir como "" (Pydantic rechaza con 422/500) ===
      // Aplica a ambos flujos (con-lae y sin-lae) porque el sin-lae también renderiza ultimo_pago_mes/anio y area_total.
      if (payload.area_total_establecimiento === "" || payload.area_total_establecimiento == null) {
        delete payload.area_total_establecimiento;
      } else {
        payload.area_total_establecimiento = Number(payload.area_total_establecimiento) || 0;
      }
      if (payload.ultimo_pago_anio === "" || payload.ultimo_pago_anio == null) {
        delete payload.ultimo_pago_anio;
      } else {
        payload.ultimo_pago_anio = Number(payload.ultimo_pago_anio);
      }
      if (payload.ultimo_pago_mes === "" || payload.ultimo_pago_mes == null) {
        delete payload.ultimo_pago_mes;
      }
      // Extraer campos internos que no van al backend directo
      const _sedes = Array.isArray(payload.sedes) ? payload.sedes : [];
      const _sedes_removidas = Array.isArray(payload._sedes_removidas_ids) ? payload._sedes_removidas_ids : [];
      delete payload.sedes;
      delete payload._sedes_removidas_ids;

      let r = null;
      if (editing) {
        r = await axios.put(`${API}/contribuyentes/${editing.id}`, payload, auth);
        // Sincronizar sedes: crear nuevas, actualizar existentes, eliminar removidas
        if (clase === "con-lae") {
          for (const rid of _sedes_removidas) {
            try {
              await axios.delete(`${API}/contribuyentes/inmuebles/${rid}`, auth);
            } catch (e) {
              const d = e.response?.data?.detail;
              const msg = typeof d === "string" ? d : "No se pudo eliminar una sede";
              toast.error(`Sede no eliminada: ${msg}`);
            }
          }
          for (const s of _sedes) {
            const sPayload = { ...s, area_total_establecimiento: Number(s.area_total_establecimiento) || 0 };
            if (s.id) {
              const upd = { ...sPayload };
              upd.metros_cuadrados = upd.area_total_establecimiento;
              upd.direccion = s.direccion_fiscal || s.direccion || "";
              delete upd.id;
              try {
                await axios.put(`${API}/contribuyentes/inmuebles/${s.id}`, upd, auth);
              } catch (e) {
                console.warn("upd sede", e);
                const d = e.response?.data?.detail;
                toast.error(`No se pudo actualizar una sede: ${typeof d === "string" ? d : ""}`);
              }
            } else {
              try {
                await axios.post(`${API}/contribuyentes/${editing.id}/sedes`, sPayload, auth);
              } catch (e) {
                console.warn("add sede", e);
                const d = e.response?.data?.detail;
                toast.error(`No se pudo agregar una sede: ${typeof d === "string" ? d : ""}`);
              }
            }
          }
        }
      } else {
        // En creación, incluir sedes en el payload principal (backend las crea atómicamente)
        if (clase === "con-lae" && _sedes.length) {
          payload.sedes = _sedes.map(s => ({
            ...s,
            area_total_establecimiento: Number(s.area_total_establecimiento) || 0,
          }));
        }
        r = await axios.post(`${API}/contribuyentes`, payload, auth);
      }
      // Feedback informativo sobre el usuario del portal (Con LAE)
      const up = r?.data?.usuario_portal;
      if (up?.creado) {
        toast.success(
          `${editing ? "Actualizado" : "Creado"}. Usuario del portal creado con clave inicial "${up.clave_temporal || "Palmaplus"}". El contribuyente deberá cambiar la contraseña y aceptar los Términos y Condiciones al primer acceso.`,
          { duration: 8000 }
        );
      } else {
        toast.success(editing ? "Actualizado" : "Creado");
      }
      setOpen(false); setEditing(null); setForm(empty); load(); onDataChange?.();
    } catch (e) {
      // Pydantic devuelve detail como array de errores → aplanar a texto legible
      const detail = e.response?.data?.detail;
      let msg = "Error";
      if (typeof detail === "string") msg = detail;
      else if (Array.isArray(detail)) msg = detail.map(x => `${(x.loc || []).slice(-1)}: ${x.msg}`).join(" · ");
      toast.error(msg);
    }
  };
  const edit = async (c) => {
    setEditing(c);
    let sedesLoaded = [];
    try {
      const r = await axios.get(`${API}/contribuyentes/${c.id}/sedes`, auth);
      sedesLoaded = Array.isArray(r.data) ? r.data : [];
      // Backfill: en sedes legacy solo existe `actividad_especifica`. Copiamos a
      // `actividad_economica` para que el nuevo Select pueda pre-seleccionar el valor.
      sedesLoaded = sedesLoaded.map(s => ({
        ...s,
        actividad_economica: s.actividad_economica || s.actividad_especifica || "",
      }));
    } catch { /* noop */ }
    setForm({ ...empty, ...c, sedes: sedesLoaded, _sedes_removidas_ids: [] });
    setOpen(true);
  };
  const suspender = async (c) => { await axios.post(`${API}/contribuyentes/${c.id}/suspender`, {}, auth); toast.success("Estado actualizado"); load(); onDataChange?.(); };
  const del = async (c) => { if (!window.confirm("¿Eliminar contribuyente e inmuebles asociados?")) return; await axios.delete(`${API}/contribuyentes/${c.id}`, auth); toast.success("Eliminado"); load(); onDataChange?.(); };
  const crearUsuarioPortal = async (c) => {
    if (!window.confirm(
      `¿Crear usuario del portal para ${c.razon_social || c.nombre}?\n\n` +
      `Email: ${c.email}\n` +
      `Clave inicial: Palmaplus\n\n` +
      `El contribuyente deberá cambiar su contraseña y aceptar los Términos y Condiciones al primer acceso.`
    )) return;
    try {
      const r = await axios.post(`${API}/contribuyentes/${c.id}/crear-usuario`, {}, auth);
      if (r.data?.created) {
        toast.success(
          `Usuario creado. Clave inicial: "${r.data?.clave_temporal || "Palmaplus"}". El contribuyente deberá cambiar la contraseña y aceptar los Términos y Condiciones al primer acceso.`,
          { duration: 8000 }
        );
      } else {
        toast.warning(r.data?.reason || "No se pudo crear el usuario");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al crear usuario del portal");
    }
  };

  const openDetail = async (c) => {
    setDetail(c);
    const r = await axios.get(`${API}/contribuyentes/${c.id}/inmuebles`, auth);
    setInmuebles(r.data);
  };
  const saveInm = async () => {
    try {
      const payload = { ...inmForm, metros_cuadrados: Number(inmForm.metros_cuadrados) || 0, contribuyente_id: detail.id };
      await axios.post(`${API}/contribuyentes/${detail.id}/inmuebles`, payload, auth);
      toast.success("Inmueble creado"); setInmOpen(false); setInmForm(emptyInm);
      const r = await axios.get(`${API}/contribuyentes/${detail.id}/inmuebles`, auth); setInmuebles(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const delInm = async (id) => { if (!window.confirm("¿Eliminar inmueble?")) return; await axios.delete(`${API}/contribuyentes/inmuebles/${id}`, auth); const r = await axios.get(`${API}/contribuyentes/${detail.id}/inmuebles`, auth); setInmuebles(r.data); };

  const isConLAE = clase === "con-lae";

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-green-700/70">
          {isConLAE
            ? "Comerciantes y empresas con Licencia de Actividad Económica activa."
            : "Contribuyentes residenciales (personas naturales sin actividad económica registrada)."}
        </p>
        <div className="flex items-center gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                data-testid={`btn-export-${clase}`}
                variant="outline"
                disabled={!items.length}
                className="border-green-200 text-green-800 hover:bg-green-50"
                title={items.length ? `Exportar ${items.length} contribuyentes` : "Sin datos para exportar"}
              >
                <Download className="w-4 h-4 mr-1" /> Exportar
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-52 p-1">
              <button data-testid={`export-excel-${clase}`} onClick={() => doExport("excel")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-green-50 text-left">
                <FileSpreadsheet className="w-4 h-4 text-green-700" /> Excel (.xlsx)
              </button>
              <button data-testid={`export-pdf-${clase}`} onClick={() => doExport("pdf")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-orange-50 text-left">
                <FileText className="w-4 h-4 text-orange-600" /> PDF (.pdf)
              </button>
              <button data-testid={`export-json-${clase}`} onClick={() => doExport("json")} className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm hover:bg-slate-50 text-left">
                <FileJson className="w-4 h-4 text-slate-600" /> JSON (.json)
              </button>
            </PopoverContent>
          </Popover>
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditing(null); setForm(empty); } }}>
            <DialogTrigger asChild>
              <Button data-testid={`btn-add-${clase}`} className="bg-orange-500 hover:bg-orange-600 text-white">
                <Plus className="w-4 h-4 mr-1" />Nuevo Contribuyente {isConLAE ? "con LAE" : "sin LAE"}
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? "Editar" : "Nuevo"} Contribuyente {isConLAE ? "con LAE" : "sin LAE"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Tipo Doc.{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label>
                <Select value={form.tipo_documento} onValueChange={v => setForm({...form, tipo_documento:v})}>
                  <SelectTrigger data-testid={`input-tipodoc-${clase}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="V">V (Venezolano)</SelectItem>
                    <SelectItem value="E">E (Extranjero)</SelectItem>
                    <SelectItem value="J">J (Jurídico)</SelectItem>
                    <SelectItem value="G">G (Gubernamental)</SelectItem>
                    <SelectItem value="P">P (Pasaporte)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{isConLAE ? <>RIF<span className="text-red-500 ml-0.5">*</span></> : <>RIF o Cédula<span className="text-red-500 ml-0.5">*</span></>}</Label>
                <Input data-testid={`input-doc-${clase}`} value={form.documento} onChange={e => setForm({...form, documento:e.target.value})}
                  className={
                    rifCheck.status === "taken" ? "border-red-400 focus-visible:ring-red-400" :
                    rifCheck.status === "available" ? "border-emerald-400 focus-visible:ring-emerald-400" : ""
                  }
                />
                {rifCheck.status === "checking" && (
                  <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1" data-testid="rif-checking"><Loader2 className="w-3 h-3 animate-spin" /> Verificando RIF...</p>
                )}
                {rifCheck.status === "available" && (
                  <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-1" data-testid="rif-available">✓ RIF disponible</p>
                )}
                {rifCheck.status === "taken" && rifCheck.contribuyente && (
                  <p className="text-[11px] text-red-700 mt-1 font-medium" data-testid="rif-taken">
                    ⚠ Ya registrado a nombre de: <span className="font-bold">{rifCheck.contribuyente.nombre}</span>
                  </p>
                )}
              </div>

              {isConLAE ? (
                <>
                  <div className="col-span-2"><Label>Razón Social / Nombre<span className="text-red-500 ml-0.5">*</span></Label><Input data-testid="input-razonsocial" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} /></div>
                  <div className="col-span-2"><Label>Denominación Comercial<span className="text-red-500 ml-0.5">*</span></Label><Input value={form.denominacion_comercial||""} onChange={e => setForm({...form, denominacion_comercial:e.target.value})} placeholder="Ej. Panadería La Espiga" /></div>
                  {/* --- RIF Imagen --- */}
                  <div className="col-span-2">
                    <Label>RIF Imagen</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,image/jpeg,application/pdf"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          if (f.size > 8 * 1024 * 1024) { toast.error("El archivo supera 8 MB"); e.target.value = ""; return; }
                          const fd = new FormData();
                          fd.append("file", f);
                          try {
                            const r = await axios.post(`${API}/recaudaciones/comprobante/upload`, fd, { ...auth, headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
                            setForm({ ...form, rif_imagen_url: r.data.url, rif_imagen_nombre: f.name });
                            toast.success("RIF cargado correctamente");
                          } catch (err) {
                            toast.error(err.response?.data?.detail || "Error al subir el archivo");
                          }
                          e.target.value = "";
                        }}
                        className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-green-600 file:text-white file:cursor-pointer hover:file:bg-green-700"
                        data-testid="input-rif-imagen"
                      />
                      {form.rif_imagen_url && (
                        <div className="flex items-center gap-2 text-xs">
                          <a
                            href={`${process.env.REACT_APP_BACKEND_URL}${form.rif_imagen_url}`}
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-medium"
                            data-testid="link-rif-imagen"
                            title={form.rif_imagen_nombre || "Ver RIF"}
                          >
                            <FileText className="w-3.5 h-3.5" /> {form.rif_imagen_nombre ? (form.rif_imagen_nombre.length > 30 ? form.rif_imagen_nombre.slice(0, 27) + "…" : form.rif_imagen_nombre) : "Ver RIF"}
                          </a>
                          <button
                            type="button"
                            onClick={() => setForm({ ...form, rif_imagen_url: "", rif_imagen_nombre: "" })}
                            className="text-red-500 hover:text-red-700"
                            data-testid="btn-rif-imagen-remove"
                            title="Quitar RIF"
                          >✕</button>
                        </div>
                      )}
                    </div>
                    <p className="text-[10px] text-slate-500 mt-1">Formatos permitidos: PDF, JPG · máx. 8 MB</p>
                  </div>
                </>
              ) : (
                <>
                  <div><Label>Nombre<span className="text-red-500 ml-0.5">*</span></Label><Input data-testid="input-nombre-sinlae" value={form.nombre} onChange={e => setForm({...form, nombre:e.target.value})} /></div>
                  <div><Label>Apellido</Label><Input value={form.apellido||""} onChange={e => setForm({...form, apellido:e.target.value})} /></div>
                </>
              )}

              <div><Label>{isConLAE ? <>Actividad Económica<span className="text-red-500 ml-0.5">*</span></> : <>Tipo de inmueble<span className="text-red-500 ml-0.5">*</span></>}</Label>
                    <Select value={form.actividad_economica||""} onValueChange={v => setForm({ ...form, actividad_economica: v })}>
                      <SelectTrigger data-testid="conlae-actividad-economica"><SelectValue placeholder={isConLAE ? "Seleccione actividad" : "Seleccione tipo de inmueble"} /></SelectTrigger>
                      <SelectContent className="max-h-80">
                        <div className="sticky top-0 bg-white p-2 border-b border-green-100 z-10">
                          <div className="flex items-center gap-2 bg-green-50 rounded-md px-2">
                            <Search className="w-3.5 h-3.5 text-green-700 shrink-0" />
                            <Input
                              data-testid="conlae-actividad-search"
                              value={actividadSearch}
                              onChange={e => setActividadSearch(e.target.value)}
                              onKeyDown={e => e.stopPropagation()}
                              placeholder="Buscar actividad…"
                              className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-7 text-sm"
                            />
                          </div>
                        </div>
                        {(() => {
                          const todas = Array.from(new Set(tarifas.map(t => t.actividad_especifica).filter(Boolean))).sort();
                          // Si el contribuyente tiene una actividad legacy que ya no existe en el catálogo,
                          // la agregamos al inicio como opción especial (marcada) para no perder el dato.
                          const legacy = form.actividad_economica && !todas.some(a => a.toUpperCase() === form.actividad_economica.toUpperCase())
                            ? form.actividad_economica
                            : null;
                          const filtradas = actividadSearch
                            ? todas.filter(a => a.toLowerCase().includes(actividadSearch.toLowerCase()))
                            : todas;
                          if (todas.length === 0 && !legacy) return <SelectItem disabled value="_empty">Sin tarifas. Registre en Aseo Urbano</SelectItem>;
                          if (filtradas.length === 0 && !legacy) return <SelectItem disabled value="_nores">Sin coincidencias</SelectItem>;
                          return (
                            <>
                              {legacy && (
                                <SelectItem key={`legacy-${legacy}`} value={legacy} className="text-amber-700 bg-amber-50 focus:bg-amber-100">
                                  {legacy} <span className="text-xs">(legacy — no en catálogo)</span>
                                </SelectItem>
                              )}
                              {filtradas.map(act => <SelectItem key={act} value={act}>{act}</SelectItem>)}
                            </>
                          );
                        })()}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>{isConLAE ? <>Área total establecimiento (m²)<span className="text-red-500 ml-0.5">*</span></> : "Área total del inmueble (m²)"}</Label><Input data-testid="conlae-area-total" type="number" min="0" step="0.01" value={form.area_total_establecimiento||""} onChange={e => setForm({...form, area_total_establecimiento:e.target.value})} placeholder="0" /></div>
                  <div className="col-span-2"><Label>Último Mes de Pago del Aseo{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label></div>
                  <div><Label className="text-xs text-slate-500">Mes</Label>
                    <Select value={form.ultimo_pago_mes||""} onValueChange={v => setForm({ ...form, ultimo_pago_mes: v })}>
                      <SelectTrigger data-testid="conlae-ultimo-pago-mes"><SelectValue placeholder="Seleccione mes" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {MESES.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs text-slate-500">Año</Label>
                    <Select value={form.ultimo_pago_anio ? String(form.ultimo_pago_anio) : ""} onValueChange={v => setForm({ ...form, ultimo_pago_anio: Number(v) })}>
                      <SelectTrigger data-testid="conlae-ultimo-pago-anio"><SelectValue placeholder="Seleccione año" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {ANIOS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

              <div>
                <Label>
                  Email{isConLAE && <span className="text-red-500 ml-0.5">*</span>}
                  {isConLAE && (
                    <span className="ml-2 text-[10px] text-slate-500 font-normal">
                      (requerido para crear acceso al portal)
                    </span>
                  )}
                </Label>
                <Input
                  type="email"
                  value={form.email||""}
                  onChange={e => setForm({...form, email:e.target.value})}
                  data-testid="contrib-email-input"
                />
              </div>
              <div><Label>Teléfono</Label><Input value={form.telefono||""} onChange={e => setForm({...form, telefono:e.target.value})} /></div>
              <div><Label>Celular{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label><Input value={form.celular||""} onChange={e => setForm({...form, celular:e.target.value})} /></div>

              <div className="col-span-2 border-t pt-3 mt-2"><p className="font-semibold text-green-900 mb-1">Ubicación Geográfica</p></div>
                  <div><Label>Estado{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label>
                    <Select value={form.estado_nombre||""} onValueChange={v => setForm({
                      ...form, estado_nombre: v,
                      municipio_id: "", municipio_nombre: "",
                      parroquia_id: "", parroquia_nombre: "",
                    })}>
                      <SelectTrigger data-testid="conlae-estado"><SelectValue placeholder="Seleccione estado" /></SelectTrigger>
                      <SelectContent>
                        {estadosUnicos.length === 0
                          ? <SelectItem disabled value="_empty">No hay estados. Registre en Definiciones → Municipios</SelectItem>
                          : estadosUnicos.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Municipio{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label>
                    <Select value={form.municipio_id||""} disabled={!form.estado_nombre} onValueChange={v => {
                      const m = municipios.find(x => x.id === v);
                      setForm({
                        ...form, municipio_id: v, municipio_nombre: m?.nombre || "",
                        parroquia_id: "", parroquia_nombre: "",
                      });
                    }}>
                      <SelectTrigger data-testid="conlae-municipio"><SelectValue placeholder={form.estado_nombre ? "Seleccione municipio" : "Primero elija estado"} /></SelectTrigger>
                      <SelectContent>
                        {municipiosDelEstado.length === 0
                          ? <SelectItem disabled value="_empty">Sin municipios en este estado</SelectItem>
                          : municipiosDelEstado.map(m => <SelectItem key={m.id} value={m.id}>{m.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Parroquia{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label>
                    <Select value={form.parroquia_id||""} disabled={!form.municipio_id} onValueChange={v => {
                      const p = parroquiasDelMunicipio.find(x => x.id === v);
                      setForm({ ...form, parroquia_id: v, parroquia_nombre: p?.nombre || "" });
                    }}>
                      <SelectTrigger data-testid="conlae-parroquia"><SelectValue placeholder={form.municipio_id ? "Seleccione parroquia" : "Primero elija municipio"} /></SelectTrigger>
                      <SelectContent>
                        {parroquiasDelMunicipio.length === 0
                          ? <SelectItem disabled value="_empty">Este municipio no tiene parroquias</SelectItem>
                          : parroquiasDelMunicipio.map(p => <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>Sector{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <SectorCombobox
                          value={form.sector || ""}
                          onChange={(v) => setForm({ ...form, sector: v })}
                          sectores={sectores}
                          emptyText="No hay sectores. Use el botón +"
                          testId="conlae-sector"
                        />
                      </div>
                      <Dialog open={newSectorOpen} onOpenChange={setNewSectorOpen}>
                        <DialogTrigger asChild>
                          <Button type="button" size="icon" variant="outline" title="Crear nuevo sector" data-testid="btn-nuevo-sector" className="border-green-200 text-green-800 hover:bg-green-50"><Plus className="w-4 h-4" /></Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-sm">
                          <DialogHeader><DialogTitle className="text-green-900">Crear nuevo sector</DialogTitle></DialogHeader>
                          <div className="space-y-2 py-2">
                            <Label>Nombre del sector*</Label>
                            <Input data-testid="input-nuevo-sector" value={newSectorName} onChange={e => setNewSectorName(e.target.value)} placeholder="Ej. José Félix Ribas" autoFocus onKeyDown={e => { if (e.key === "Enter") saveNewSector(); }} />
                            <p className="text-xs text-slate-500">El sector quedará disponible en toda la plataforma.</p>
                          </div>
                          <DialogFooter>
                            <Button variant="outline" onClick={() => setNewSectorOpen(false)} disabled={savingSector}>Cancelar</Button>
                            <Button data-testid="btn-guardar-sector" onClick={saveNewSector} disabled={savingSector} className="bg-green-700 hover:bg-green-800 text-white">{savingSector ? "Guardando..." : "Guardar"}</Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                  <div className="col-span-2"><Label>Domicilio Fiscal{isConLAE && <span className="text-red-500 ml-0.5">*</span>}</Label><Input value={form.direccion_fiscal||""} onChange={e => setForm({...form, direccion_fiscal:e.target.value})} /></div>

              {isConLAE && (
                <>
                  <div className="col-span-2"><Label>Punto de Referencia</Label><Input value={form.punto_referencia||""} onChange={e => setForm({...form, punto_referencia:e.target.value})} /></div>
                  <div className="col-span-2 border-t pt-3 mt-2"><p className="font-semibold text-green-900 mb-1">Representante Legal</p></div>
                  <div><Label>Cédula Representante</Label><Input value={form.cedula_representante_legal||""} onChange={e => setForm({...form, cedula_representante_legal:e.target.value})} /></div>
                  <div><Label>Nombre Representante</Label><Input value={form.nombre_representante_legal||""} onChange={e => setForm({...form, nombre_representante_legal:e.target.value})} /></div>
                  <div><Label>Celular Representante</Label><Input value={form.celular_representante_legal||""} onChange={e => setForm({...form, celular_representante_legal:e.target.value})} /></div>
                </>
              )}
              <div className="col-span-2 border-t pt-3 mt-2"><p className="font-semibold text-green-900 mb-1">Códigos de Identificación</p></div>
              <div><Label>Código Catastral</Label>
                <CatastralInput
                  value={form.codigo_catastral || ""}
                  onChange={v => setForm({...form, codigo_catastral: v})}
                  disabled={form.sin_codigo_catastral}
                  testId="conlae-catastral"
                />
              </div>
              <div className="flex items-center gap-2 pt-7">
                <input type="checkbox" id="sin-codigo" checked={form.sin_codigo_catastral||false} onChange={e => setForm({...form, sin_codigo_catastral:e.target.checked, codigo_catastral: e.target.checked ? "" : form.codigo_catastral})} className="accent-green-700 w-4 h-4" />
                <Label htmlFor="sin-codigo" className="cursor-pointer">Sin código catastral</Label>
              </div>
              <div className="col-span-2"><Label>Cuenta contrato electricidad</Label><Input data-testid="input-cuenta-electricidad" value={form.cuenta_contrato_electricidad||""} onChange={e => setForm({...form, cuenta_contrato_electricidad:e.target.value})} placeholder="N° de cuenta contrato del servicio eléctrico" /></div>

              {/* --- Documento de arrendamiento / Ficha o Cédula Catastral --- */}
              <div className="col-span-2">
                <Label>Contrato de arrendamiento ó Ficha o Cédula Catastral</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,image/jpeg,application/pdf"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      if (f.size > 8 * 1024 * 1024) { toast.error("El archivo supera 8 MB"); e.target.value = ""; return; }
                      const fd = new FormData();
                      fd.append("file", f);
                      try {
                        const r = await axios.post(`${API}/recaudaciones/comprobante/upload`, fd, { ...auth, headers: { ...auth.headers, "Content-Type": "multipart/form-data" } });
                        setForm({ ...form, documento_catastro_url: r.data.url, documento_catastro_nombre: f.name });
                        toast.success("Documento cargado correctamente");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Error al subir el archivo");
                      }
                      e.target.value = "";
                    }}
                    className="text-sm file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:bg-green-600 file:text-white file:cursor-pointer hover:file:bg-green-700"
                    data-testid="input-doc-catastro"
                  />
                  {form.documento_catastro_url && (
                    <div className="flex items-center gap-2 text-xs">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${form.documento_catastro_url}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 font-medium"
                        data-testid="link-doc-catastro"
                        title={form.documento_catastro_nombre || "Ver documento"}
                      >
                        <FileText className="w-3.5 h-3.5" /> {form.documento_catastro_nombre ? (form.documento_catastro_nombre.length > 30 ? form.documento_catastro_nombre.slice(0, 27) + "…" : form.documento_catastro_nombre) : "Ver documento"}
                      </a>
                      <button
                        type="button"
                        onClick={() => setForm({ ...form, documento_catastro_url: "", documento_catastro_nombre: "" })}
                        className="text-red-500 hover:text-red-700"
                        data-testid="btn-doc-catastro-remove"
                        title="Quitar documento"
                      >✕</button>
                    </div>
                  )}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Formatos permitidos: PDF, JPG · máx. 8 MB</p>
              </div>

              <div className="col-span-2"><Label>Observaciones</Label><Textarea value={form.observaciones||""} onChange={e => setForm({...form, observaciones:e.target.value})} rows={2} /></div>

              {/* ============ SEDES / SUCURSALES (solo Con LAE) ============ */}
              {isConLAE && (
                <SedesEditor
                  sedes={form.sedes || []}
                  onChange={(sedes, removedId) => setForm(f => ({
                    ...f,
                    sedes,
                    _sedes_removidas_ids: removedId
                      ? [ ...(f._sedes_removidas_ids || []), removedId ]
                      : (f._sedes_removidas_ids || []),
                  }))}
                  municipios={municipios}
                  sectores={sectores}
                  tarifas={tarifas}
                  auth={auth}
                  API={API}
                  onSectorCreated={(nuevo) => setSectores(prev =>
                    [...prev, nuevo].sort((a, b) => a.nombre.localeCompare(b.nombre))
                  )}
                />
              )}
            </div>
            <DialogFooter>
              {rifCheck.status === "taken" && (
                <span className="text-xs text-red-600 mr-auto self-center font-medium">
                  ⚠ Corrija el RIF antes de guardar
                </span>
              )}
              <Button
                data-testid={`btn-save-${clase}`}
                onClick={save}
                disabled={rifCheck.status === "taken" || rifCheck.status === "checking"}
                className="bg-green-700 hover:bg-green-800 text-white disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editing ? "Actualizar" : "Crear"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <Card className="p-4 border-green-100">
        {isConLAE ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-2 mb-4" data-testid="conlae-filtros">
            <Input data-testid="filter-rif" value={filters.rif} onChange={e => setFilters({...filters, rif:e.target.value})} placeholder="RIF (ej: 40842628)" className="bg-green-50/60 border-green-100 h-9" />
            <Input data-testid="filter-razon-social" value={filters.razon_social} onChange={e => setFilters({...filters, razon_social:e.target.value})} placeholder="Razón social" className="bg-green-50/60 border-green-100 h-9" />
            <Input data-testid="filter-denominacion" value={filters.denominacion_comercial} onChange={e => setFilters({...filters, denominacion_comercial:e.target.value})} placeholder="Denominación" className="bg-green-50/60 border-green-100 h-9" />
            <Input data-testid="filter-direccion" value={filters.direccion_fiscal} onChange={e => setFilters({...filters, direccion_fiscal:e.target.value})} placeholder="Domicilio fiscal" className="bg-green-50/60 border-green-100 h-9" />
            <div className="flex gap-2">
              <Select value={filters.is_active} onValueChange={v => setFilters({...filters, is_active:v})}>
                <SelectTrigger data-testid="filter-estado" className="bg-green-50/60 border-green-100 h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="activos">Activos</SelectItem>
                  <SelectItem value="suspendidos">Suspendidos</SelectItem>
                </SelectContent>
              </Select>
              {(filters.rif || filters.razon_social || filters.denominacion_comercial || filters.direccion_fiscal || filters.is_active !== "todos") && (
                <Button size="sm" variant="ghost" onClick={() => setFilters({ rif:"", razon_social:"", denominacion_comercial:"", direccion_fiscal:"", is_active:"todos" })} data-testid="filter-clear" title="Limpiar filtros" className="text-slate-500 hover:text-red-600 h-9 px-2">✕</Button>
              )}
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-green-50 rounded-lg px-3 py-2 mb-4">
            <Search className="w-4 h-4 text-green-700" />
            <Input data-testid={`input-search-${clase}`} value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre, documento, email…" className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-8" />
          </div>
        )}
        {/* Paginación superior */}
        <Paginacion
          page={page} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          total={total} totalPages={totalPages}
          loading={loading}
          position="top"
        />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900"><tr>
              {isConLAE ? (
                <>
                  <SortableTh field="documento" label="RIF" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
                  <SortableTh field="razon_social" label="Razón Social" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
                  <SortableTh field="denominacion_comercial" label="Denominación" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
                  <th className="text-left p-2.5">Domicilio Fiscal</th>
                </>
              ) : (
                <>
                  <SortableTh field="documento" label="Documento" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
                  <SortableTh field="nombre" label="Nombre" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
                  <th className="text-left p-2.5">Email</th>
                </>
              )}
              <th className="text-left p-2.5">Sector</th><th className="text-center p-2.5">Estado</th><th className="text-right p-2.5">Acciones</th>
            </tr></thead>
            <tbody>{loading ? (
              <tr><td colSpan={isConLAE ? 7 : 6} className="p-10 text-center text-green-600">Cargando…</td></tr>
            ) : items.map(c => (
              <tr key={c.id} className="border-b border-green-50 hover:bg-green-50/40" data-testid={`row-${clase}-${c.documento}`}>
                {isConLAE ? (
                  <>
                    <td className="p-2.5 font-mono">{c.tipo_documento}-{c.documento}</td>
                    <td className="p-2.5 font-medium">
                      <div className="flex items-center gap-1.5">
                        <span>{c.razon_social || c.nombre}</span>
                        {c.has_portal_user === false && (
                          <span
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-300"
                            title={c.email ? "Sin acceso al portal — haga click en el botón 🔑 para crear el usuario" : "Sin email registrado — no puede crear usuario del portal"}
                            data-testid={`badge-sin-portal-${c.documento}`}
                          >
                            SIN ACCESO
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="p-2.5 text-slate-600">{c.denominacion_comercial||"-"}</td>
                    <td className="p-2.5 text-slate-600 max-w-xs truncate" title={c.direccion_fiscal||""}>{c.direccion_fiscal||"-"}</td>
                  </>
                ) : (
                  <>
                    <td className="p-2.5 font-mono">{c.tipo_documento}-{c.documento}</td>
                    <td className="p-2.5 font-medium">{c.nombre} {c.apellido||""}</td>
                    <td className="p-2.5 text-slate-600">{c.email||"-"}</td>
                  </>
                )}
                <td className="p-2.5">{c.sector||"-"}</td>
                <td className="p-2.5 text-center">{c.is_active ? <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Activo</Badge> : <Badge className="bg-rose-100 text-rose-800 hover:bg-rose-100">Suspendido</Badge>}</td>
                <td className="p-2.5 text-right">
                  <div className="inline-flex gap-1">
                    {isConLAE && (
                      <Button size="icon" variant="ghost" onClick={() => setViewing(c)} title="Ver detalles" data-testid={`btn-ver-${c.documento}`}><Eye className="w-4 h-4 text-emerald-700" /></Button>
                    )}
                    {isConLAE && c.has_portal_user === false && c.email && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => crearUsuarioPortal(c)}
                        title="Crear usuario del portal (clave inicial: Palmaplus)"
                        data-testid={`btn-crear-usuario-${c.documento}`}
                      >
                        <KeyRound className="w-4 h-4 text-amber-700" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => edit(c)} title="Editar"><Edit className="w-4 h-4 text-blue-600" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => suspender(c)} title={c.is_active ? "Suspender" : "Reactivar"}>{c.is_active ? <Ban className="w-4 h-4 text-orange-600" /> : <CheckCircle className="w-4 h-4 text-green-700" />}</Button>
                    <Button size="icon" variant="ghost" onClick={() => del(c)} title="Eliminar"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  </div>
                </td>
              </tr>
            ))}{!loading && !items.length && <tr><td colSpan={isConLAE ? 7 : 6} className="p-8 text-center text-slate-400">Sin contribuyentes {isConLAE ? "con LAE" : "sin LAE"} registrados</td></tr>}</tbody>
          </table>
        </div>
        {/* Paginación inferior */}
        <Paginacion
          page={page} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          total={total} totalPages={totalPages}
          loading={loading}
          position="bottom"
        />
      </Card>

      {/* ================== DIÁLOGO: VER DETALLES (solo lectura) ================== */}
      <Dialog open={!!viewing} onOpenChange={(o) => { if (!o) setViewing(null); }}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="dialog-ver-contribuyente">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-900">
              <Eye className="w-5 h-5 text-emerald-700" /> Detalles del Contribuyente
            </DialogTitle>
          </DialogHeader>
          {viewing && <VerContribuyenteDetalle c={viewing} />}
          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setViewing(null)} data-testid="btn-cerrar-ver">Cerrar</Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!detail} onOpenChange={(o) => { if(!o) { setDetail(null); setInmuebles([]); } }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Inmuebles de {detail?.nombre} {detail?.apellido||""}</DialogTitle></DialogHeader>
          <div className="flex justify-end mb-2">
            <Dialog open={inmOpen} onOpenChange={setInmOpen}>
              <DialogTrigger asChild><Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white"><Plus className="w-4 h-4 mr-1" />Inmueble</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Nuevo inmueble</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Dirección*</Label><Input value={inmForm.direccion} onChange={e => setInmForm({...inmForm, direccion:e.target.value})} /></div>
                  <div><Label>Metros cuadrados</Label><Input type="number" value={inmForm.metros_cuadrados} onChange={e => setInmForm({...inmForm, metros_cuadrados:e.target.value})} /></div>
                  <div><Label>Tipo de zona</Label>
                    <Select value={inmForm.tipo_zona} onValueChange={v => setInmForm({...inmForm, tipo_zona:v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="residencial">Residencial</SelectItem><SelectItem value="comercial">Comercial</SelectItem><SelectItem value="industrial">Industrial</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2"><Label>Actividad específica (Aseo Urbano)</Label>
                    <Select value={inmForm.actividad_especifica} onValueChange={v => {
                      const t = tarifas.find(x => x.actividad_especifica === v);
                      setInmForm({ ...inmForm, actividad_especifica: v, codigo_clasificacion_aseo: t?.codigo_clasificacion || "" });
                    }}>
                      <SelectTrigger><SelectValue placeholder="Seleccionar actividad" /></SelectTrigger>
                      <SelectContent className="max-h-72">{tarifas.map(t => <SelectItem key={t.id} value={t.actividad_especifica}>{t.codigo_clasificacion} · {t.actividad_especifica}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Código catastral</Label>
                    <CatastralInput
                      value={inmForm.codigo_catastral || ""}
                      onChange={v => setInmForm({...inmForm, codigo_catastral: v})}
                      testId="inmueble-catastral"
                    />
                  </div>
                  <div><Label>Sector</Label><Input value={inmForm.sector||""} onChange={e => setInmForm({...inmForm, sector:e.target.value})} /></div>
                </div>
                <DialogFooter><Button onClick={saveInm} className="bg-green-700 hover:bg-green-800 text-white">Guardar</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {inmuebles.map(i => (
              <div key={i.id} className="flex items-center justify-between p-3 bg-green-50/50 rounded-lg border border-green-100">
                <div>
                  <p className="font-medium text-green-900">{i.direccion}</p>
                  <p className="text-xs text-green-700/70">{i.tipo_zona} · {i.metros_cuadrados} m² · {i.actividad_especifica || "Sin clasificación"}</p>
                </div>
                <Button size="icon" variant="ghost" onClick={() => delInm(i.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
              </div>
            ))}
            {!inmuebles.length && <p className="text-center text-slate-400 py-6">Sin inmuebles registrados</p>}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}



// ==================== Componentes auxiliares de paginación ====================
function SortableTh({ field, label, sort, order, onChange, align = "left" }) {
  const active = sort === field;
  const next = active && order === "asc" ? "desc" : "asc";
  const Icon = active ? (order === "asc" ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <th
      className={`text-${align} p-2.5 cursor-pointer select-none hover:bg-green-100 transition-colors`}
      onClick={() => onChange(field, next)}
      data-testid={`sort-${field}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <Icon className={`w-3 h-3 ${active ? "text-green-800" : "text-green-400"}`} />
      </span>
    </th>
  );
}

function Paginacion({ page, setPage, pageSize, setPageSize, total, totalPages, loading, position = "bottom" }) {
  if (total === 0 && !loading) return null;
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);
  const nfmt = (n) => Number(n || 0).toLocaleString("es-VE");
  const windowSize = 5;
  let from = Math.max(1, page - Math.floor(windowSize / 2));
  let to = Math.min(totalPages, from + windowSize - 1);
  from = Math.max(1, to - windowSize + 1);
  const pages = [];
  for (let p = from; p <= to; p++) pages.push(p);
  const isTop = position === "top";
  const wrapperCls = isTop
    ? "flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-3 border-b border-green-100 pb-3 text-sm"
    : "flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-4 border-t border-green-100 pt-3 text-sm";

  return (
    <div className={wrapperCls} data-testid={`paginacion-bar-${position}`}>
      <div className="flex items-center gap-3 text-green-800">
        <span data-testid={`paginacion-info-${position}`}>
          {total === 0 ? "Sin resultados" : (<>Mostrando <b>{nfmt(start)}</b>-<b>{nfmt(end)}</b> de <b>{nfmt(total)}</b></>)}
        </span>
        <span className="text-green-700/60">·</span>
        <span>Página <b>{page}</b> de <b>{nfmt(totalPages || 1)}</b></span>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1 mr-2">
          <span className="text-green-700 text-xs">Filas:</span>
          <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
            <SelectTrigger className="h-8 w-[80px]" data-testid={`page-size-select-${position}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              {[25, 50, 100, 200].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button size="icon" variant="outline" onClick={() => setPage(1)} disabled={page <= 1 || loading} data-testid={`page-first-${position}`}><ChevronsLeft className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1 || loading} data-testid={`page-prev-${position}`}><ChevronLeft className="w-4 h-4" /></Button>
        {pages.map(p => (
          <Button
            key={p}
            size="sm"
            variant={p === page ? "default" : "outline"}
            className={p === page ? "bg-green-700 hover:bg-green-800 text-white h-8 min-w-[36px]" : "h-8 min-w-[36px]"}
            onClick={() => setPage(p)}
            disabled={loading}
            data-testid={`page-btn-${position}-${p}`}
          >
            {p}
          </Button>
        ))}
        <Button size="icon" variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page >= totalPages || loading} data-testid={`page-next-${position}`}><ChevronRight className="w-4 h-4" /></Button>
        <Button size="icon" variant="outline" onClick={() => setPage(totalPages)} disabled={page >= totalPages || loading} data-testid={`page-last-${position}`}><ChevronsRight className="w-4 h-4" /></Button>
      </div>
    </div>
  );
}

// ==================== Lista Pre-Aprobados (registros públicos desde landing) ====================
function ListaPreAprobados({ auth, API, onDataChange }) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [detail, setDetail] = useState(null);
  const [rechazoOpen, setRechazoOpen] = useState(false);
  const [rechazoTarget, setRechazoTarget] = useState(null);
  const [rechazoMotivo, setRechazoMotivo] = useState("");
  const [aprobarOpen, setAprobarOpen] = useState(false);
  const [aprobarTarget, setAprobarTarget] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {
      clase: "con-lae", estado_registro: "pre_aprobado",
      paginated: true, page, page_size: pageSize, sort, order,
    };
    if (search.trim()) params.razon_social = search.trim();
    setLoading(true);
    return axios.get(`${API}/contribuyentes`, { ...auth, params })
      .then(r => {
        setItems(r.data.items || []);
        setTotal(r.data.total || 0);
        setTotalPages(r.data.total_pages || 0);
      })
      .catch(() => { setItems([]); setTotal(0); setTotalPages(0); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, page, pageSize, sort, order]);

  useEffect(() => { setPage(1); /* eslint-disable-next-line */ }, [search, pageSize]);

  const openAprobar = (c) => {
    setAprobarTarget(c);
    setAprobarOpen(true);
  };

  const confirmarAprobar = async () => {
    if (!aprobarTarget) return;
    try {
      setBusy(true);
      await axios.post(`${API}/contribuyentes/${aprobarTarget.id}/aprobar-pre`, {}, auth);
      toast.success(`✔ ${aprobarTarget.razon_social || aprobarTarget.nombre} aprobado. Cuenta activada.`);
      setAprobarOpen(false);
      setAprobarTarget(null);
      setDetail(null);
      load();
      onDataChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo aprobar");
    } finally { setBusy(false); }
  };

  const openRechazo = (c) => {
    setRechazoTarget(c);
    setRechazoMotivo("");
    setRechazoOpen(true);
  };

  const confirmarRechazo = async () => {
    const motivo = rechazoMotivo.trim();
    if (!motivo) { toast.error("Debe indicar un motivo"); return; }
    try {
      setBusy(true);
      await axios.post(`${API}/contribuyentes/${rechazoTarget.id}/rechazar-pre`, { motivo }, auth);
      toast.success(`Registro rechazado: ${rechazoTarget.razon_social || rechazoTarget.nombre}`);
      setRechazoOpen(false);
      setRechazoTarget(null);
      setRechazoMotivo("");
      setDetail(null);
      load();
      onDataChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo rechazar");
    } finally { setBusy(false); }
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-green-700/70">
            Registros públicos pendientes de aprobación. Cuando apruebe, la cuenta del contribuyente quedará activa para acceder al Portal.
          </p>
          {total > 0 && (
            <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
              <AlertCircle className="w-3.5 h-3.5" />
              <b>{total}</b> registro{total === 1 ? "" : "s"} esperando revisión
            </p>
          )}
        </div>
      </div>

      <Card className="p-4 border-amber-100 bg-amber-50/20">
        <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 mb-4 border border-amber-100">
          <Search className="w-4 h-4 text-amber-700" />
          <Input
            data-testid="input-search-pre-aprobado"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar por razón social…"
            className="bg-transparent border-none focus-visible:ring-0 focus-visible:ring-offset-0 h-8"
          />
        </div>

        <Paginacion
          page={page} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          total={total} totalPages={totalPages}
          loading={loading}
          position="top"
        />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-amber-100 text-amber-900"><tr>
              <SortableTh field="created_at" label="Fecha" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
              <SortableTh field="documento" label="RIF" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
              <SortableTh field="razon_social" label="Razón Social" sort={sort} order={order} onChange={(f, o) => { setSort(f); setOrder(o); }} />
              <th className="text-left p-2.5">Actividad</th>
              <th className="text-left p-2.5">Contacto</th>
              <th className="text-left p-2.5">Ubicación</th>
              <th className="text-center p-2.5">Docs</th>
              <th className="text-right p-2.5">Acciones</th>
            </tr></thead>
            <tbody>{loading ? (
              <tr><td colSpan={8} className="p-10 text-center text-amber-700">Cargando…</td></tr>
            ) : items.map(c => (
              <tr key={c.id} className="border-b border-amber-100 hover:bg-amber-50/60" data-testid={`row-pre-aprobado-${c.documento}`}>
                <td className="p-2.5 text-xs text-slate-600 whitespace-nowrap">{(c.created_at || "").slice(0, 10)}</td>
                <td className="p-2.5 font-mono">{c.tipo_documento}-{c.documento}</td>
                <td className="p-2.5">
                  <div className="font-medium text-green-900">{c.razon_social || c.nombre || "—"}</div>
                  {c.denominacion_comercial && <div className="text-xs text-slate-500">{c.denominacion_comercial}</div>}
                  <div className="flex flex-wrap gap-1 mt-1">
                    {c.origen_precargado && (
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-[9px] py-0 px-1.5" title="Completó datos desde el portal · Origen Precargado" data-testid={`badge-precargado-${c.documento}`}>
                        Origen Precargado
                      </Badge>
                    )}
                    {c.update_count > 0 && !c.origen_precargado && (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100 text-[9px] py-0 px-1.5" title={`Actualizó datos ${c.update_count} vez/veces (última vez desde ${c.last_update_from_state || "-"})`}>
                        {c.update_count === 1 ? "Actualizado" : `Actualizado (${c.update_count}×)`}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="p-2.5 text-slate-600 text-xs max-w-[180px] truncate" title={c.actividad_economica || ""}>{c.actividad_economica || "—"}</td>
                <td className="p-2.5 text-xs">
                  {c.email && <div className="truncate max-w-[180px]" title={c.email}>{c.email}</div>}
                  {c.celular && <div className="text-slate-600">{c.celular}</div>}
                </td>
                <td className="p-2.5 text-xs text-slate-600 max-w-[160px] truncate" title={`${c.parroquia_nombre || ""}, ${c.municipio_nombre || ""}`}>
                  {c.sector || "—"}<br /><span className="text-slate-400">{c.parroquia_nombre || ""}</span>
                </td>
                <td className="p-2.5 text-center">
                  <div className="inline-flex gap-1">
                    {c.rif_imagen_url && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${c.rif_imagen_url}`}
                        target="_blank" rel="noopener noreferrer"
                        title="Ver RIF"
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-blue-100 hover:bg-blue-200 text-blue-800 text-[10px] font-medium"
                        data-testid={`doc-rif-${c.documento}`}
                      >
                        <FileText className="w-3 h-3" /> RIF
                      </a>
                    )}
                    {c.documento_catastro_url && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${c.documento_catastro_url}`}
                        target="_blank" rel="noopener noreferrer"
                        title="Ver Ficha Catastral / Arrendamiento"
                        className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-emerald-100 hover:bg-emerald-200 text-emerald-800 text-[10px] font-medium"
                        data-testid={`doc-catastro-${c.documento}`}
                      >
                        <FileText className="w-3 h-3" /> Cat.
                      </a>
                    )}
                    {!c.rif_imagen_url && !c.documento_catastro_url && <span className="text-slate-400 text-xs">—</span>}
                  </div>
                </td>
                <td className="p-2.5 text-right">
                  <div className="inline-flex gap-1">
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => setDetail(c)}
                      title="Ver detalle completo"
                      data-testid={`btn-ver-pre-${c.documento}`}
                      className="h-8 px-2 text-slate-700 hover:bg-slate-100"
                    ><Eye className="w-4 h-4" /></Button>
                    <Button
                      size="sm" variant="ghost"
                      onClick={async () => {
                        try {
                          const r = await axios.get(`${API}/contribuyentes/${c.id}/verificacion-pdf`, { ...auth, responseType: "blob" });
                          const blob = new Blob([r.data], { type: "application/pdf" });
                          const url = URL.createObjectURL(blob);
                          const w = window.open(url, "_blank");
                          if (w) { w.onload = () => { try { w.print(); } catch { /* noop */ } }; }
                          setTimeout(() => URL.revokeObjectURL(url), 60000);
                        } catch (e) {
                          toast.error(e.response?.status === 403 ? "Sin permiso para imprimir." : "No se pudo generar el PDF de verificación.");
                        }
                      }}
                      title="Imprimir checklist de verificación"
                      data-testid={`btn-imprimir-pre-${c.documento}`}
                      className="h-8 px-2 text-indigo-700 hover:bg-indigo-50"
                    ><Printer className="w-4 h-4" /></Button>
                    <Button
                      size="sm"
                      onClick={() => openAprobar(c)}
                      disabled={busy}
                      title="Aprobar registro"
                      data-testid={`btn-aprobar-pre-${c.documento}`}
                      className="h-8 px-2 bg-green-700 hover:bg-green-800 text-white"
                    ><ThumbsUp className="w-4 h-4 mr-1" />Aprobar</Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => openRechazo(c)}
                      disabled={busy}
                      title="Rechazar registro"
                      data-testid={`btn-rechazar-pre-${c.documento}`}
                      className="h-8 px-2 border-red-200 text-red-700 hover:bg-red-50"
                    ><ThumbsDown className="w-4 h-4 mr-1" />Rechazar</Button>
                  </div>
                </td>
              </tr>
            ))}{!loading && !items.length && (
              <tr><td colSpan={8} className="p-10 text-center text-slate-400">
                <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-300" />
                <p>No hay registros pendientes de aprobación.</p>
                <p className="text-xs mt-1">Los registros llegan desde la Landing Page pública.</p>
              </td></tr>
            )}</tbody>
          </table>
        </div>

        <Paginacion
          page={page} setPage={setPage}
          pageSize={pageSize} setPageSize={setPageSize}
          total={total} totalPages={totalPages}
          loading={loading}
          position="bottom"
        />
      </Card>

      {/* Diálogo de detalle */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="dialog-detalle-pre">
          <DialogHeader>
            <DialogTitle className="text-green-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Solicitud de registro — {detail?.razon_social || detail?.nombre}
            </DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="col-span-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <p className="text-xs font-semibold text-amber-900 mb-1">Estado del registro</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-amber-500 text-white">PRE-APROBADO</Badge>
                    {detail.origen_precargado && (
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100" data-testid="detail-badge-precargado" title="Este registro venía cargado desde Excel; el ciudadano completó sus datos desde el portal">
                        Completó datos desde el portal · Origen Precargado
                      </Badge>
                    )}
                    {detail.update_count > 0 && !detail.origen_precargado && (
                      <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">
                        Actualizó datos {detail.update_count}× (última vez desde {detail.last_update_from_state || "-"})
                      </Badge>
                    )}
                    <span className="text-xs text-amber-800">Enviado el {(detail.created_at || "").slice(0, 10)} · Pendiente de aprobación</span>
                  </div>
                </div>

                <Info label="Tipo Doc." value={detail.tipo_documento} />
                <Info label="RIF" value={detail.documento} mono />
                <Info label="Razón Social" value={detail.razon_social || detail.nombre} full />
                <Info label="Denominación Comercial" value={detail.denominacion_comercial} full />
                <Info label="Actividad Económica" value={detail.actividad_economica} full />
                <Info label="Área Establecimiento (m²)" value={detail.area_total_establecimiento} />
                <Info label="Último Pago" value={detail.ultimo_pago_mes ? `${detail.ultimo_pago_mes} ${detail.ultimo_pago_anio || ""}` : "—"} />
                <Info label="Email" value={detail.email} />
                <Info label="Celular" value={detail.celular} />
                <Info label="Teléfono" value={detail.telefono || "—"} />

                <div className="col-span-2 border-t pt-2 mt-1"><p className="font-semibold text-green-900">Ubicación</p></div>
                <Info label="Estado" value={detail.estado_nombre} />
                <Info label="Municipio" value={detail.municipio_nombre} />
                <Info label="Parroquia" value={detail.parroquia_nombre} />
                <Info label="Sector" value={detail.sector} />
                <Info label="Domicilio Fiscal" value={detail.direccion_fiscal} full />
                {detail.punto_referencia && <Info label="Punto de Referencia" value={detail.punto_referencia} full />}

                <div className="col-span-2 border-t pt-2 mt-1"><p className="font-semibold text-green-900">Representante Legal</p></div>
                <Info label="Cédula" value={detail.cedula_representante_legal || "—"} />
                <Info label="Nombre" value={detail.nombre_representante_legal || "—"} />
                <Info label="Celular" value={detail.celular_representante_legal || "—"} />

                <div className="col-span-2 border-t pt-2 mt-1"><p className="font-semibold text-green-900">Códigos e Identificación</p></div>
                <Info label="Código Catastral" value={detail.codigo_catastral || (detail.sin_codigo_catastral ? "Sin código" : "—")} />
                <Info label="Cuenta Contrato Elec." value={detail.cuenta_contrato_electricidad || "—"} />

                <div className="col-span-2 border-t pt-2 mt-1"><p className="font-semibold text-green-900">Documentos anexos</p></div>
                <div className="col-span-2 flex flex-wrap gap-2">
                  {detail.rif_imagen_url ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${detail.rif_imagen_url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-100 hover:bg-blue-200 text-blue-900 text-xs font-medium"
                      data-testid="detalle-doc-rif"
                    >
                      <FileText className="w-4 h-4" /> RIF: {detail.rif_imagen_nombre || "Ver archivo"}
                    </a>
                  ) : <span className="text-xs text-slate-400">RIF: no anexado</span>}
                  {detail.documento_catastro_url ? (
                    <a
                      href={`${process.env.REACT_APP_BACKEND_URL}${detail.documento_catastro_url}`}
                      target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-100 hover:bg-emerald-200 text-emerald-900 text-xs font-medium"
                      data-testid="detalle-doc-catastro"
                    >
                      <FileText className="w-4 h-4" /> Ficha/Arrend.: {detail.documento_catastro_nombre || "Ver archivo"}
                    </a>
                  ) : <span className="text-xs text-slate-400">Ficha Catastral: no anexada</span>}
                </div>

                {detail.observaciones && (
                  <div className="col-span-2 border-t pt-2 mt-1">
                    <p className="font-semibold text-green-900 mb-1">Observaciones del solicitante</p>
                    <p className="text-sm text-slate-700 bg-slate-50 rounded p-2">{detail.observaciones}</p>
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setDetail(null)}
              disabled={busy}
              data-testid="btn-cerrar-detalle-pre"
            >Cerrar</Button>
            <Button
              variant="outline"
              onClick={() => openRechazo(detail)}
              disabled={busy}
              className="border-red-200 text-red-700 hover:bg-red-50"
              data-testid="btn-rechazar-detalle-pre"
            ><ThumbsDown className="w-4 h-4 mr-1" />Rechazar</Button>
            <Button
              onClick={() => openAprobar(detail)}
              disabled={busy}
              className="bg-green-700 hover:bg-green-800 text-white"
              data-testid="btn-aprobar-detalle-pre"
            ><ThumbsUp className="w-4 h-4 mr-1" />Aprobar y activar cuenta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo de rechazo con motivo */}
      <Dialog open={rechazoOpen} onOpenChange={(o) => { setRechazoOpen(o); if (!o) { setRechazoTarget(null); setRechazoMotivo(""); } }}>
        <DialogContent className="max-w-md" data-testid="dialog-rechazo-pre">
          <DialogHeader>
            <DialogTitle className="text-red-800 flex items-center gap-2">
              <ThumbsDown className="w-5 h-5" />
              Rechazar registro
            </DialogTitle>
          </DialogHeader>
          {rechazoTarget && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                Va a rechazar el registro de <b>{rechazoTarget.razon_social || rechazoTarget.nombre}</b> ({rechazoTarget.tipo_documento}-{rechazoTarget.documento}).
                La cuenta quedará <b>inactiva</b> y el motivo se registrará en su historial.
              </p>
              <div>
                <Label>Motivo del rechazo <span className="text-red-500">*</span></Label>
                <Textarea
                  value={rechazoMotivo}
                  onChange={e => setRechazoMotivo(e.target.value)}
                  rows={4}
                  placeholder="Ej: Documentos ilegibles, RIF no coincide, actividad no corresponde…"
                  data-testid="input-motivo-rechazo"
                  autoFocus
                />
                <p className="text-[10px] text-slate-500 mt-1">El motivo quedará auditado como parte del rechazo.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRechazoOpen(false)}
              disabled={busy}
              data-testid="btn-cancelar-rechazo"
            >Cancelar</Button>
            <Button
              onClick={confirmarRechazo}
              disabled={busy || !rechazoMotivo.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="btn-confirmar-rechazo"
            >{busy ? "Rechazando…" : "Confirmar rechazo"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Diálogo de confirmación de aprobación */}
      <Dialog open={aprobarOpen} onOpenChange={(o) => { setAprobarOpen(o); if (!o) setAprobarTarget(null); }}>
        <DialogContent className="max-w-md" data-testid="dialog-aprobar-pre">
          <DialogHeader>
            <DialogTitle className="text-green-900 flex items-center gap-2">
              <ThumbsUp className="w-5 h-5 text-green-700" />
              Confirmar aprobación
            </DialogTitle>
          </DialogHeader>
          {aprobarTarget && (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">
                Va a aprobar el registro de <b>{aprobarTarget.razon_social || aprobarTarget.nombre}</b> ({aprobarTarget.tipo_documento}-{aprobarTarget.documento}).
              </p>
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-xs text-green-900">
                <ul className="list-disc list-inside space-y-1">
                  <li>La cuenta del contribuyente será <b>activada</b>.</li>
                  <li>Podrá iniciar sesión en el Portal con las credenciales que definió al registrarse.</li>
                  <li>Aparecerá en la lista principal <b>Con LAE (Comerciantes)</b>.</li>
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setAprobarOpen(false)}
              disabled={busy}
              data-testid="btn-cancelar-aprobar"
            >Cancelar</Button>
            <Button
              onClick={confirmarAprobar}
              disabled={busy}
              className="bg-green-700 hover:bg-green-800 text-white"
              data-testid="btn-confirmar-aprobar"
            >{busy ? "Aprobando…" : "Confirmar aprobación"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Info({ label, value, full = false, mono = false }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <p className="text-[10px] uppercase text-slate-500 font-medium tracking-wide">{label}</p>
      <p className={`text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value || "—"}</p>
    </div>
  );
}

